import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { abrirOuDescarregar, abrirFicheiroDaResposta } from '../utils/abrirFicheiro';

/* ============================================================
   ACIDENTES, INCIDENTES E COMUNICAÇÕES (SGI — ISO 45001, 10.2/9.1 · P_GSI_03)
   Registo e investigação fiéis ao RG.10 (Relatório de Investigação
   de Acidente), Mapa de Incidentes M.GME.05.B/01 (colunas A..AC, mais
   AD..AI do registo de comunicação) e índices de sinistralidade
   mensais. Só os acidentes de trabalho (fora de in itinere) contam
   para os índices.
   Também recebe as comunicações que substituem o Microsoft Forms
   "SPROD - Registo de Comunicação / Incidentes": reclamações, não
   conformidades e outros (melhoria / sugestão) — sem sinistrado,
   lesão, riscos, baixa nem RG.10; seguem para o Plano de melhoria
   com "Criar NC".
   Os registos feitos no telemóvel (/registo-incidente) chegam aqui
   com origem 'mobile' e estado 'aberto'.
   ============================================================ */

// Tipo = "ORIGEM" do Forms. label = texto do Mapa/Excel; curto = etiqueta da tabela de registos
const TIPOS = {
    acidente: { label: 'Acidente de trabalho', curto: 'Acidente', novo: 'Registar acidente de trabalho', bg: '#fee2e2', fg: '#991b1b' },
    quase_acidente: { label: 'Quase-acidente', curto: 'Quase-acidente', novo: 'Registar quase-acidente', bg: '#fef9c3', fg: '#854d0e' },
    incidente: { label: 'Incidente SST / condição perigosa', curto: 'Incidente SST', novo: 'Registar incidente SST / condição perigosa', bg: '#e0f2fe', fg: '#075985' },
    reclamacao: { label: 'Reclamação', curto: 'Reclamação', novo: 'Registar reclamação', bg: '#fce7f3', fg: '#9d174d' },
    nao_conformidade: { label: 'Não conformidade', curto: 'Não conformidade', novo: 'Registar não conformidade', bg: '#ffedd5', fg: '#9a3412' },
    outro: { label: 'Outros (melhoria / sugestão)', curto: 'Melhoria / sugestão', novo: 'Registar melhoria / sugestão', bg: '#ccfbf1', fg: '#115e59' },
};
const TIPOS_SST = ['acidente', 'quase_acidente', 'incidente'];
const TIPOS_COMUNICACAO = ['reclamacao', 'nao_conformidade', 'outro'];
const GRUPOS_TIPO = [['Segurança (SST)', TIPOS_SST], ['Comunicação', TIPOS_COMUNICACAO]];
const eSst = (tipo) => TIPOS_SST.includes(tipo);
const infoTipo = (tipo) => TIPOS[tipo] || TIPOS.acidente;
// Filtro do separador Registos
const FILTROS_TIPO = [['todos', 'Todos'], ['sst', 'SST'], ['reclamacao', 'Reclamações'], ['nao_conformidade', 'Não conformidades'], ['outro', 'Outros']];
/* O que cada tipo mostra e grava (as mesmas regras do mobile e da validação do backend).
   O PUT é parcial (campo ausente = fica como está): o que está escondido vai a null/vazio
   no pedido, para não ficar lixo de um tipo anterior. */
const regrasTipo = (tipo) => {
    const acidenteOuQuase = tipo === 'acidente' || tipo === 'quase_acidente';
    return {
        sst: eSst(tipo),             // envolvido, riscos, 1.ºs socorros, análise IPAR, investigação RG.10
        lesao: acidenteOuQuase,      // tipo e local da lesão
        socorro: acidenteOuQuase,    // meios de socorro (112 / INEM)
        baixa: tipo === 'acidente' || tipo === 'incidente',   // consequência e baixa (o quase-acidente é sem baixa)
    };
};
// DEPARTAMENTO / ÁREA do Forms
const DEPARTAMENTOS = { comercial: 'Comercial', financeiro: 'Financeiro', rh: 'RH', implementacao: 'Implementação' };
const nomeDepartamento = (d) => DEPARTAMENTOS[d] || d || '';
// "Criar NC": reclamação → origem "Reclamação de cliente"; outros (melhoria) → Oportunidade de Melhoria (OM)
const infoNc = (tipo) => ({
    sigla: tipo === 'outro' ? 'OM' : 'NC',
    nome: tipo === 'outro' ? 'Oportunidade de Melhoria' : 'Não Conformidade',
    origem: tipo === 'reclamacao' ? 'Reclamação de cliente' : 'Registo de incidente',
    conteudo: eSst(tipo) ? 'a descrição, as causas e as medidas (ou o plano de ação)' : 'a descrição, o plano de ação e o prazo',
});
const CONSEQUENCIAS = {
    sem_ita: 'Sem ITA (sem baixa)',
    ita: 'ITA ≤ 30 dias — incapacidade temporária absoluta (baixa)',
    itp: 'ITP — incapacidade temporária parcial',
    ipp: 'IPP — incapacidade permanente parcial',
    ipa: 'IPA — incapacidade permanente absoluta',
    mortal: 'Mortal',
};
const CONSEQ_CURTA = { sem_ita: 'Sem ITA', ita: 'ITA', itp: 'ITP', ipp: 'IPP', ipa: 'IPA', mortal: 'Mortal' };
const COM_BAIXA = ['ita', 'itp', 'ipp', 'ipa', 'mortal'];
const GRAVES = ['mortal', 'ipa', 'ipp'];
const COM_GRAU = ['itp', 'ipp'];
const ESTADOS = {
    aberto: { label: 'Aberto', bg: '#fee2e2', fg: '#991b1b' },
    em_investigacao: { label: 'Em investigação', bg: '#fef9c3', fg: '#854d0e' },
    fechado: { label: 'Fechado', bg: '#dcfce7', fg: '#166534' },
};
const VINCULOS = { interno: 'Interno', subcontratado: 'Subcontratado' };
const PARTICIPACOES = [['seguradora', 'Participação Seguradora'], ['act', 'Participação ACT'], ['apcer', 'Participação APCER']];
// Checklist de riscos do RG.10 (textos como no documento)
const RISCO_COLAPSO = 'Colapso de …';
const RISCO_OUTRO = 'Outro';
const RISCOS_RG10 = [
    'Queda em altura >2m', 'Queda a diferente nível', 'Queda ao mesmo nível', 'Tropeção sobre objetos',
    'Marcha s/ solo irregular', 'Queda de objetos', 'Golpe, Corte e/ou Perfuração', 'Choque contra/por objetos',
    'Projeções de partículas', 'Esmagamento', 'Capotamento', 'Agarramento', 'Contactos elétricos',
    'Queimadura térmica', 'Explosão / Incêndio', 'Colisão com veículos', 'Atropelamento', 'Acidente de tráfego',
    'Sobreesforços físico', 'Sobreesforços postural', 'Inalação subst. Nocivas', 'Intoxicação',
    RISCO_COLAPSO, RISCO_OUTRO,
];
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MAX_FOTOS = 6;
const MAX_TESTEMUNHAS = 4;
const DIA_MS = 86400000;

/* ---------- helpers ---------- */
// Datas 'YYYY-MM-DD' tratadas como texto/UTC (sem desvios de fuso)
const dISO = (d) => (d ? String(d).substring(0, 10) : '');
const fmtData = (d) => {
    const s = dISO(d);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
    const [a, m, dia] = s.split('-');
    return `${dia}/${m}/${a}`;
};
// Timestamps (ex.: criado_em, TIMESTAMPTZ em ISO UTC) na hora de Lisboa — igual ao dataHoraPt do RG.10
const fmtDataHora = (ts) => {
    const d = ts ? new Date(ts) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    try {
        return d.toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return d.toISOString().slice(0, 16).replace('T', ' '); }
};
const fmtNum = (v, casas = 1) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toLocaleString('pt-PT', { maximumFractionDigits: casas }) : '—';
};
const fmtIdx = (v) => (v == null || v === '' ? '—' : fmtNum(v, 2));
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const lista = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.trim()) {
        try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
    }
    return [];
};
// Perguntas S/N: true | false | null  <->  'true' | 'false' | ''
const snIn = (v) => (v === true ? 'true' : v === false ? 'false' : '');
const snOut = (v) => (v === 'true' ? true : v === 'false' ? false : null);
const simNao = (v) => (v === true ? 'Sim' : v === false ? 'Não' : '—');

const utc = (iso) => Date.parse(`${iso}T00:00:00Z`);
// N.º de dias seg–sex no intervalo (inclusive), como o NETWORKDAYS do Excel (sem feriados)
const diasUteisEntre = (iniISO, fimISO) => {
    const a = utc(iniISO), b = utc(fimISO);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
    let n = 0;
    for (let t = a; t <= b; t += DIA_MS) {
        const wd = new Date(t).getUTCDay();
        if (wd !== 0 && wd !== 6) n++;
    }
    return n;
};
const diasUteisMes = (ano, mes) => {
    const ultimo = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
    const mm = String(mes).padStart(2, '0');
    return diasUteisEntre(`${ano}-${mm}-01`, `${ano}-${mm}-${String(ultimo).padStart(2, '0')}`);
};
// Regra da baixa (igual ao Excel): com início e fim → AA=(fim−início)+1, AB=NETWORKDAYS, AC=AB×8
const calcBaixa = (f) => {
    if (f.baixa_inicio && f.baixa_fim) {
        const a = utc(f.baixa_inicio), b = utc(f.baixa_fim);
        if (!Number.isFinite(a) || !Number.isFinite(b)) return { auto: false, dias_perdidos: 0, dias_uteis: 0, horas_perdidas: 0 };
        if (b < a) return { auto: true, erro: 'A data de fim da baixa é anterior à data de início.', dias_perdidos: 0, dias_uteis: 0, horas_perdidas: 0 };
        const du = diasUteisEntre(f.baixa_inicio, f.baixa_fim);
        return { auto: true, dias_perdidos: Math.round((b - a) / DIA_MS) + 1, dias_uteis: du, horas_perdidas: du * 8 };
    }
    const dp = Math.max(parseInt(f.dias_perdidos, 10) || 0, 0);
    const du = Math.max(parseInt(f.dias_uteis, 10) || 0, 0);
    return { auto: false, dias_perdidos: dp, dias_uteis: du, horas_perdidas: du * 8 };
};
// Regra de comunicação: acidente ⇒ Seguradora; grave (mortal/IPA/IPP ou ≥30 dias) ⇒ ACT e APCER.
// As comunicações (reclamação, não conformidade, outros) não têm participações obrigatórias.
const alertasComunicacao = (tipo, consequencia, diasPerdidos, participacoes) => {
    if (!eSst(tipo)) return { grave: false, seguradora: false, act: false, apcer: false };
    const p = new Set(participacoes);
    const grave = GRAVES.includes(consequencia) || num(diasPerdidos) >= 30;
    return {
        grave,
        seguradora: tipo === 'acidente' && !p.has('seguradora'),
        act: grave && !p.has('act'),
        apcer: grave && !p.has('apcer'),
    };
};
const alertasDoRegisto = (i) => alertasComunicacao(i.tipo, i.consequencia, i.dias_perdidos, lista(i.participacoes_lista));
/* Riscos — espelho do analisarRiscos/textoRiscos do backend (utils/incidentes.js), usado no
   mapa.xlsx e no RG.10: ordem da checklist, "Colapso de <texto>" e "Outro: <texto>", itens
   desconhecidos juntam-se a "Outro". Chave tolerante: sem acentos, maiúsculas, pontuação nem 's'. */
const RISCOS_CHECK = RISCOS_RG10.filter(r => r !== RISCO_COLAPSO && r !== RISCO_OUTRO);
const chaveRisco = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]/g, '').replace(/s/g, '');
const CHAVES_RISCOS = RISCOS_CHECK.map(chaveRisco);
// "Colapso de andaime" → "andaime"; "Colapso de …" → ''
const textoColapso = (s) => String(s).replace(/^colapso\s*(de)?\s*[:.…]*\s*/i, '').replace(/_+/g, '').trim();
const textoOutroRisco = (s) => String(s).replace(/^outros?\s*[.:]?\s*(qual\s*[:?]?)?\s*/i, '').replace(/_+/g, '').trim();
const analisarRiscos = (riscos, riscosOutro) => {
    const partes = String(riscos || '').split(/[;\n]/).map(s => s.trim()).filter(Boolean);
    const marcados = new Set();
    let colapso = null, outro = null;
    const soltos = [];
    for (const p of partes) {
        const k = chaveRisco(p);
        const idx = CHAVES_RISCOS.indexOf(k);
        if (idx >= 0) { marcados.add(idx); continue; }
        if (k.startsWith('colapo')) { colapso = textoColapso(p); continue; }
        if (k.startsWith('outro')) { outro = textoOutroRisco(p); continue; }
        soltos.push(p);
    }
    const ro = String(riscosOutro || '').trim();
    if (colapso !== null && outro !== null) {
        if (!outro) outro = ro;
        else if (!colapso) colapso = ro;
    } else if (colapso !== null) {
        if (!colapso) colapso = ro;
    } else if (outro !== null) {
        if (!outro) outro = ro;
    } else if (ro) {
        outro = ro;
    }
    if (soltos.length) outro = [outro, ...soltos].filter(Boolean).join('; ');
    return { marcados, colapso, outro };
};
// Coluna J do Mapa: usa o texto do servidor quando vem (riscos_texto, igual ao Excel), senão o espelho local
const textoRiscos = (i) => {
    if (typeof i.riscos_texto === 'string') return i.riscos_texto || '—';
    const a = analisarRiscos(i.riscos, i.riscos_outro);
    const partes = RISCOS_CHECK.filter((_, idx) => a.marcados.has(idx));
    if (a.colapso !== null) partes.push(`Colapso de ${a.colapso || '…'}`);
    if (a.outro !== null) partes.push(`Outro: ${a.outro || '…'}`);
    return partes.join('; ') || '—';
};
/* Estado inicial da checklist do modal: itens do RG.10 (maiúsculas/minúsculas normalizadas),
   "Colapso de <texto>" → caixa "Colapso de …" + texto próprio; o texto de "Outro" fica em
   riscos_outro. Registos antigos guardavam o texto do colapso em riscos_outro (só com
   "Colapso de …" marcado) e o backend trata riscos_outro sem caixa marcada como "Outro". */
const lerRiscosIniciais = (inc) => {
    const porChave = new Map(RISCOS_RG10.map(r => [r.toLowerCase(), r]));
    const itens = String(inc?.riscos || '').split(/[;\n]/).map(s => s.trim()).filter(Boolean);
    const eColapso = (s) => s.toLowerCase().startsWith('colapso');
    const itemColapso = itens.find(eColapso);
    const temOutro = itens.some(s => s.toLowerCase().startsWith('outro'));
    let colapso = itemColapso ? textoColapso(itemColapso) : '';
    let outro = inc?.riscos_outro || '';
    if (itemColapso && !colapso && !temOutro) { colapso = outro; outro = ''; }
    const sel = new Set(itens.map(s => porChave.get(s.toLowerCase()) || (eColapso(s) ? RISCO_COLAPSO : s)));
    if (!itemColapso && !temOutro && outro) sel.add(RISCO_OUTRO);
    return { sel, colapso, outro };
};
const textoTestemunhas = (i) => {
    const l = lista(i.testemunhas_lista).filter(t => t && t.nome);
    if (l.length) return l.map(t => (t.funcao ? `${t.nome} (${t.funcao})` : t.nome)).join('; ');
    return i.testemunhas || '—';
};
const nomeEnvolvido = (i) => i.trabalhador_nome || i.nome || '—';
const nomeComunicou = (i) => i.reportado_nome || i.reportado_por_nome || '';
// O RG.10 aplica-se aos tipos SST (o servidor recusa-o às comunicações)
const podeRg10 = (i) => eSst(i?.tipo);
// "A executar em" ultrapassado num registo ainda não fechado (data local, como o input date)
const hojeISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const planoEmAtraso = (i) => !!dISO(i.plano_prazo) && i.estado !== 'fechado' && dISO(i.plano_prazo) < hojeISO();

// Mensagem de erro de um pedido com responseType 'blob'
const erroDeBlob = async (e, fallback) => {
    const d = e?.response?.data;
    if (d instanceof Blob) {
        try { return JSON.parse(await d.text()).error || fallback; } catch { return fallback; }
    }
    return d?.error || fallback;
};
const abrirRg10 = async (i) => {
    try {
        const r = await api.get(`/sistema-integrado/incidentes/${i.id}/rg10.pdf`, { responseType: 'blob', timeout: 60000 });
        abrirFicheiroDaResposta(r, `RG10_${i.numero || i.id}.pdf`);
    } catch (e) { alert(await erroDeBlob(e, 'Erro ao gerar o RG.10')); }
};

// Redimensionar/comprimir foto no cliente (máx. 1280px, JPEG q0.7); null se não for imagem legível
const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
            const maxDim = 1280;
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const scale = maxDim / Math.max(width, height);
                width = Math.round(width * scale);
                height = Math.round(height * scale);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => resolve(null);
        img.src = reader.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
});

const Campo = ({ label, children, style }) => (
    <div style={style}>
        <label data-liquid-label="true" style={lbl}><LiquidText>{label}</LiquidText></label>
        {children}
    </div>
);
const SelSN = ({ value, onChange }) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={inpW}>
        <option value="">—</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
    </select>
);
const testemunhaVazia = () => ({ trabalhador_id: '', nome: '', funcao: '' });
const medidaVazia = () => ({ acao: '', prazo: '', responsavel: '', concluida: false });

/* ---------- Registo / investigação (RG.10) ---------- */
function IncidenteModal({ incidente, tipoInicial, onClose, onSaved }) {
    const { user } = useAuth();
    const novo = !incidente;
    const incId = incidente?.id;
    const [opcoes, setOpcoes] = useState({ trabalhadores: [], subcontratados: [], obras: [], carregadas: false });
    const [opcoesErro, setOpcoesErro] = useState(false);
    const [riscosIni] = useState(() => lerRiscosIniciais(incidente));
    /* O estado guarda tudo (mudar de tipo não apaga nada e voltar atrás recupera os campos);
       o que o tipo esconde não segue no pedido — ver montarPayload. */
    const [form, setForm] = useState(() => ({
        tipo: incidente?.tipo || tipoInicial || 'acidente',
        data_ocorrencia: dISO(incidente?.data_ocorrencia),
        hora: incidente?.hora || '',
        departamento: incidente?.departamento || '',
        // NOME do Forms ("Comunicado por"): ao criar, o utilizador atual — editável
        reportado_nome: incidente ? (incidente.reportado_nome || '') : (user?.nome_completo || user?.username || ''),
        obra_id: incidente?.obra_id ? String(incidente.obra_id) : '',
        projeto_ref: incidente?.projeto_ref || '',
        local: incidente?.local || '',
        in_itinere: !!incidente?.in_itinere,
        id_ocorrencia: incidente?.id_ocorrencia || '',
        vinculo: incidente?.vinculo === 'subcontratado' ? 'subcontratado' : 'interno',
        trabalhador_id: incidente?.trabalhador_id ? String(incidente.trabalhador_id) : '',
        subcontratado_id: incidente?.subcontratado_id ? String(incidente.subcontratado_id) : '',
        nome: incidente?.nome || incidente?.trabalhador_nome || '',
        funcao: incidente?.funcao || '',
        empregador: incidente ? (incidente.empregador || '') : 'SPROD',
        sexo: incidente?.sexo || '',
        tarefa_habitual: snIn(incidente?.tarefa_habitual),
        circunstancias: incidente?.circunstancias || '',
        descricao: incidente?.descricao || '',
        atividade: incidente?.atividade || '',
        primeiros_socorros: snIn(incidente?.primeiros_socorros),
        assistencia_medica: snIn(incidente?.assistencia_medica),
        meios_socorro: snIn(incidente?.meios_socorro),
        danos_materiais: incidente?.danos_materiais || '',
        plano_acao: incidente?.plano_acao || '',
        plano_prazo: dISO(incidente?.plano_prazo),
        lesao: incidente?.lesao || '',
        local_lesao: incidente?.local_lesao || '',
        riscos_outro: riscosIni.outro,
        similares: snIn(incidente?.similares),
        controlo_operacional: snIn(incidente?.controlo_operacional),
        risco_ipar: snIn(incidente?.risco_ipar),
        reavaliacao_ipar: snIn(incidente?.reavaliacao_ipar),
        comunicacao_interna: snIn(incidente?.comunicacao_interna),
        causas: incidente?.causas || '',
        consequencia: incidente?.consequencia || 'sem_ita',
        grau_incapacidade: incidente?.grau_incapacidade != null ? String(parseFloat(incidente.grau_incapacidade)) : '',
        baixa_inicio: dISO(incidente?.baixa_inicio),
        baixa_fim: dISO(incidente?.baixa_fim),
        dias_perdidos: String(incidente?.dias_perdidos ?? 0),
        dias_uteis: String(incidente?.dias_uteis ?? 0),
        paragem_atividade: !!incidente?.paragem_atividade,
        participacao_outro: incidente?.participacao_outro || '',
        tecnico: incidente?.tecnico || '',
        data_investigacao: dISO(incidente?.data_investigacao),
        rsi_nome: incidente?.rsi_nome || '',
        data_validacao_rsi: dISO(incidente?.data_validacao_rsi),
        estado: incidente?.estado || 'aberto',
        observacoes: incidente?.observacoes || '',
    }));
    // Checklist de risco do RG.10 (sem perder itens desconhecidos); "Colapso de …" tem texto próprio
    const [riscosSel, setRiscosSel] = useState(() => new Set(riscosIni.sel));
    const [colapsoTxt, setColapsoTxt] = useState(riscosIni.colapso);
    const [testemunhas, setTestemunhas] = useState(() => {
        const l = lista(incidente?.testemunhas_lista).filter(t => t && (t.nome || t.trabalhador_id))
            .slice(0, MAX_TESTEMUNHAS)
            .map(t => ({ trabalhador_id: t.trabalhador_id ? String(t.trabalhador_id) : '', nome: t.nome || '', funcao: t.funcao || '' }));
        while (l.length < 2) l.push(testemunhaVazia());
        return l;
    });
    const [participacoes, setParticipacoes] = useState(() => new Set(lista(incidente?.participacoes_lista)));
    const [medidas, setMedidas] = useState(() => {
        const l = lista(incidente?.medidas_lista).filter(m => m && m.acao)
            .map(m => ({ acao: m.acao || '', prazo: dISO(m.prazo), responsavel: m.responsavel || '', concluida: !!m.concluida }));
        return l.length ? l : [medidaVazia()];
    });
    const fotosIniciais = useMemo(() => lista(incidente?.fotos), [incidente]);
    // Fotos já gravadas: o índice é a posição no array guardado (endpoint /fotos/:idx)
    const [fotosExistentes, setFotosExistentes] = useState(() => fotosIniciais.map((caminho, idx) => ({ caminho, idx })));
    const [fotosUrls, setFotosUrls] = useState({});       // idx -> objectURL | null (erro)
    const [fotosNovas, setFotosNovas] = useState([]);     // data URLs (JPEG comprimido)
    const [aProcessarFotos, setAProcessarFotos] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    useEffect(() => {
        let ativo = true;
        api.get('/sistema-integrado/incidentes/opcoes')
            .then(r => {
                if (!ativo) return;
                setOpcoes({
                    trabalhadores: r.data?.trabalhadores || [],
                    subcontratados: r.data?.subcontratados || [],
                    obras: r.data?.obras || [],
                    carregadas: true,
                });
            })
            .catch(() => { if (ativo) setOpcoesErro(true); });
        return () => { ativo = false; };
    }, []);

    // Miniaturas das fotos gravadas (pasta protegida → pedido autenticado → objectURL, revogado ao fechar)
    useEffect(() => {
        if (!incId || fotosIniciais.length === 0) return undefined;
        let ativo = true;
        const criadas = [];
        fotosIniciais.forEach((_, idx) => {
            api.get(`/sistema-integrado/incidentes/${incId}/fotos/${idx}`, { responseType: 'blob', timeout: 30000 })
                .then(r => {
                    if (!ativo) return;
                    const url = URL.createObjectURL(r.data);
                    criadas.push(url);
                    setFotosUrls(prev => ({ ...prev, [idx]: url }));
                })
                .catch(() => { if (ativo) setFotosUrls(prev => ({ ...prev, [idx]: null })); });
        });
        return () => { ativo = false; criadas.forEach(u => URL.revokeObjectURL(u)); };
    }, [incId, fotosIniciais]);

    const vis = regrasTipo(form.tipo);
    const nc = infoNc(form.tipo);
    const baixa = calcBaixa(form);
    // Valores efetivos (os que se gravam): a consequência e a baixa só contam onde se mostram
    const consequenciaEf = vis.baixa ? form.consequencia : 'sem_ita';
    const diasEf = vis.baixa ? baixa.dias_perdidos : 0;
    const alertas = alertasComunicacao(form.tipo, consequenciaEf, diasEf, participacoes);
    const riscosExtra = [...riscosSel].filter(r => !RISCOS_RG10.includes(r));
    const totalFotos = fotosExistentes.length + fotosNovas.length;
    const ncCodigo = incidente?.nc_codigo;
    const planoTxt = form.plano_acao.trim();
    const planoNasMedidas = !!planoTxt && medidas.some(m => m.acao.trim() === planoTxt);
    // Numeração das secções visíveis (muda com o tipo)
    const ordemSecoes = ['ocorrencia', vis.sst && 'envolvido', 'testemunhas', 'descricao', 'plano',
        vis.sst && 'risco', 'analise', vis.sst && 'investigacao', 'seguimento', 'fotos'].filter(Boolean);
    const nSec = (k) => ordemSecoes.indexOf(k) + 1;

    const toggleSet = (setter, v) => setter(prev => { const n = new Set(prev); if (n.has(v)) n.delete(v); else n.add(v); return n; });

    // Ao trocar de vínculo limpa o que vinha do trabalhador/empresa escolhidos
    const mudarVinculo = (v) => setForm(prev => ({
        ...prev, vinculo: v, trabalhador_id: '', subcontratado_id: '',
        nome: prev.trabalhador_id ? '' : prev.nome,
        funcao: prev.trabalhador_id ? '' : prev.funcao,
        empregador: v === 'interno' ? 'SPROD' : '',
    }));
    const escolherTrabalhador = (id) => {
        const t = opcoes.trabalhadores.find(x => String(x.id) === id);
        setForm(prev => ({
            ...prev, trabalhador_id: id,
            nome: t ? t.nome : prev.nome,
            funcao: t ? (t.funcao || '') : prev.funcao,
            empregador: t ? (t.empresa || 'SPROD') : prev.empregador,
        }));
    };
    const escolherSubcontratado = (id) => {
        const s = opcoes.subcontratados.find(x => String(x.id) === id);
        setForm(prev => ({ ...prev, subcontratado_id: id, empregador: s ? s.nome : prev.empregador }));
    };

    const updTestemunha = (idx, patch) => setTestemunhas(prev => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
    const escolherTestemunha = (idx, id) => {
        const t = opcoes.trabalhadores.find(x => String(x.id) === id);
        updTestemunha(idx, t ? { trabalhador_id: id, nome: t.nome, funcao: t.funcao || '' } : { trabalhador_id: '' });
    };
    const updMedida = (idx, patch) => setMedidas(prev => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
    // Plano de ação e melhoria → nova medida do RG.10 (prazo = "A executar em", responsável = quem comunicou)
    const copiarPlanoParaMedidas = () => {
        if (!planoTxt) return;
        const nova = { acao: planoTxt, prazo: form.plano_prazo || '', responsavel: form.reportado_nome.trim(), concluida: false };
        setMedidas(prev => {
            if (prev.some(m => m.acao.trim() === planoTxt)) return prev;
            // a linha vazia inicial não fica antes da medida copiada
            const preenchidas = prev.filter(m => m.acao.trim() || m.prazo || m.responsavel.trim() || m.concluida);
            return [...preenchidas, nova];
        });
    };

    const juntarFotos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;
        const livres = MAX_FOTOS - totalFotos;
        if (livres <= 0) { alert(`Máximo de ${MAX_FOTOS} fotos por registo.`); return; }
        if (files.length > livres) alert(`Só pode juntar mais ${livres} foto${livres === 1 ? '' : 's'} (máx. ${MAX_FOTOS}).`);
        setAProcessarFotos(true);
        let falhadas = 0;
        const novas = [];
        for (const file of files.slice(0, livres)) {
            const b64 = await compressImage(file);
            if (b64) novas.push(b64); else falhadas++;
        }
        setFotosNovas(prev => [...prev, ...novas].slice(0, Math.max(MAX_FOTOS - fotosExistentes.length, 0)));
        setAProcessarFotos(false);
        if (falhadas) alert(`${falhadas} ficheiro${falhadas === 1 ? '' : 's'} não ${falhadas === 1 ? 'é uma imagem válida' : 'são imagens válidas'}.`);
    };

    /* Pedido completo: o que o tipo esconde vai explicitamente a null / vazio / false (o PUT é
       parcial e manteria os valores antigos). O estado do formulário não se perde: se voltar a
       mudar o tipo antes de guardar, os campos reaparecem preenchidos. */
    const montarPayload = () => {
        const { sst, lesao, socorro, baixa: comBaixa } = vis;
        const interno = form.vinculo === 'interno';
        const colapso = colapsoTxt.replace(/[;\n]+/g, ',').trim();
        const grau = parseFloat(String(form.grau_incapacidade).replace(',', '.'));
        const txtSe = (mostra, v) => (mostra ? v.trim() : null);
        const snSe = (mostra, v) => (mostra ? snOut(v) : null);
        const dataSe = (mostra, v) => (mostra && v ? v : null);
        const payload = {
            // Comuns a todos os tipos (perguntas do Forms + ocorrência)
            tipo: form.tipo,
            data_ocorrencia: form.data_ocorrencia || null,
            hora: form.hora.trim(),
            departamento: form.departamento || null,
            reportado_nome: form.reportado_nome.trim(),
            obra_id: form.obra_id ? parseInt(form.obra_id, 10) : null,
            projeto_ref: form.projeto_ref.trim(),
            local: form.local.trim(),
            circunstancias: form.circunstancias.trim(),
            descricao: form.descricao.trim(),
            plano_acao: planoTxt,
            plano_prazo: form.plano_prazo || null,
            testemunhas_lista: testemunhas.filter(t => t.nome.trim()).map(t => ({
                trabalhador_id: t.trabalhador_id ? parseInt(t.trabalhador_id, 10) : null, nome: t.nome.trim(), funcao: t.funcao.trim(),
            })),
            similares: snOut(form.similares),
            comunicacao_interna: snOut(form.comunicacao_interna),
            estado: form.estado,
            observacoes: form.observacoes.trim(),
            // Só SST: Mapa de Incidentes, sinistrado / envolvido, circunstâncias da ocorrência
            in_itinere: sst && form.in_itinere,
            id_ocorrencia: txtSe(sst, form.id_ocorrencia),
            vinculo: sst ? form.vinculo : 'interno',
            trabalhador_id: sst && interno && form.trabalhador_id ? parseInt(form.trabalhador_id, 10) : null,
            subcontratado_id: sst && !interno && form.subcontratado_id ? parseInt(form.subcontratado_id, 10) : null,
            nome: txtSe(sst, form.nome),
            funcao: txtSe(sst, form.funcao),
            empregador: txtSe(sst, form.empregador),
            sexo: sst && form.sexo ? form.sexo : null,
            tarefa_habitual: snSe(sst, form.tarefa_habitual),
            atividade: txtSe(sst, form.atividade),
            primeiros_socorros: snSe(sst, form.primeiros_socorros),
            assistencia_medica: snSe(sst, form.assistencia_medica),
            meios_socorro: snSe(socorro, form.meios_socorro),
            danos_materiais: txtSe(sst, form.danos_materiais),
            // Lesão só em acidente / quase-acidente; risco em todos os SST.
            // "Colapso de <texto>" leva o seu texto no próprio item; riscos_outro só para "Outro"
            lesao: txtSe(lesao, form.lesao),
            local_lesao: txtSe(lesao, form.local_lesao),
            riscos: sst ? [...riscosSel].map(r => (r === RISCO_COLAPSO && colapso ? `Colapso de ${colapso}` : r)).join('; ') : null,
            riscos_outro: sst && riscosSel.has(RISCO_OUTRO) ? (form.riscos_outro.trim() || null) : null,
            controlo_operacional: snSe(sst, form.controlo_operacional),
            risco_ipar: snSe(sst, form.risco_ipar),
            reavaliacao_ipar: snSe(sst, form.reavaliacao_ipar),
            // Investigação (RG.10) só SST; consequência e baixa só em acidente / incidente
            causas: txtSe(sst, form.causas),
            consequencia: consequenciaEf,
            grau_incapacidade: comBaixa && COM_GRAU.includes(form.consequencia) && Number.isFinite(grau) ? grau : null,
            baixa_inicio: dataSe(comBaixa, form.baixa_inicio),
            baixa_fim: dataSe(comBaixa, form.baixa_fim),
            dias_perdidos: diasEf,
            dias_uteis: comBaixa ? baixa.dias_uteis : 0,
            paragem_atividade: sst && form.paragem_atividade,
            participacoes_lista: sst ? PARTICIPACOES.map(([k]) => k).filter(k => participacoes.has(k)) : [],
            participacao_outro: txtSe(sst, form.participacao_outro),
            medidas_lista: sst ? medidas.filter(m => m.acao.trim()).map(m => ({
                acao: m.acao.trim(), prazo: m.prazo || null, responsavel: m.responsavel.trim(), concluida: !!m.concluida,
            })) : [],
            tecnico: txtSe(sst, form.tecnico),
            data_investigacao: dataSe(sst, form.data_investigacao),
            rsi_nome: txtSe(sst, form.rsi_nome),
            data_validacao_rsi: dataSe(sst, form.data_validacao_rsi),
            fotos_base64: fotosNovas,
        };
        if (!novo) payload.fotos_manter = fotosExistentes.map(f => f.caminho);
        return payload;
    };

    // criarNc: grava primeiro (a NC nasce dos dados gravados) e depois cria a NC (ou OM)
    const guardar = async (criarNc = false) => {
        if (!form.data_ocorrencia) { setErr('Indique a data da ocorrência.'); return; }
        if (!form.departamento) { setErr('Indique o departamento / área.'); return; }
        if (!form.descricao.trim()) { setErr('Preencha a descrição do incidente.'); return; }
        if (vis.baixa && baixa.erro) { setErr(baixa.erro); return; }
        // Linhas meio preenchidas seriam descartadas ao gravar sem aviso (as medidas só existem nos tipos SST)
        const mi = vis.sst ? medidas.findIndex(m => !m.acao.trim() && (m.prazo || m.responsavel.trim() || m.concluida)) : -1;
        if (mi >= 0) { setErr(`Medida n.º ${mi + 1}: indique a ação (ou remova a linha).`); return; }
        const ti = testemunhas.findIndex(t => !t.nome.trim() && t.funcao.trim());
        if (ti >= 0) { setErr(`Testemunha ${ti + 1}: indique o nome (ou remova a linha).`); return; }
        if (criarNc && !window.confirm(`${novo ? 'Gravar o registo' : 'Gravar as alterações'} e criar uma ${nc.nome} (origem "${nc.origem}") com ${nc.conteudo} deste registo?`)) return;
        setSaving(true); setErr('');
        try {
            const opts = { timeout: fotosNovas.length ? 90000 : 30000 };
            const r = novo
                ? await api.post('/sistema-integrado/incidentes', montarPayload(), opts)
                : await api.put(`/sistema-integrado/incidentes/${incId}`, montarPayload(), opts);
            const idGravado = incId || r?.data?.id;
            if (criarNc && idGravado) {
                try { await api.post(`/sistema-integrado/incidentes/${idGravado}/criar-nc`); }
                catch (e2) { alert(e2.response?.data?.error || `O registo foi gravado, mas houve um erro ao criar a ${nc.nome}.`); }
            }
            onSaved(r?.data);
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
            setSaving(false);
        }
    };

    const tp = infoTipo(form.tipo);
    const trabInativo = form.vinculo === 'interno' && form.trabalhador_id
        && !opcoes.trabalhadores.some(t => String(t.id) === form.trabalhador_id);
    const subInativo = form.vinculo === 'subcontratado' && form.subcontratado_id
        && !opcoes.subcontratados.some(s => String(s.id) === form.subcontratado_id);
    const obraFora = form.obra_id && !opcoes.obras.some(o => String(o.id) === form.obra_id);
    // Registos antigos podem apontar para quem já não está nas listas (só se sabe depois de carregar)
    const sufixoFora = opcoes.carregadas ? ' (inativo)' : '';

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay}>
            <div style={{ ...modal, maxWidth: 940 }}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>
                        {novo ? tp.novo : `${incidente.numero || 'Registo'} — ${infoTipo(incidente.tipo).label}`}
                    </h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn} title="Fechar"><LiquidText>✕</LiquidText></button>
                </div>
                {incidente?.origem === 'mobile' && (
                    <div style={{ ...aviso, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}><LiquidText>
                        📱 Reportado no telemóvel por </LiquidText><strong>{nomeComunicou(incidente) || '—'}</strong>
                        {incidente.reportado_nome && incidente.reportado_por_nome && incidente.reportado_nome !== incidente.reportado_por_nome
                            ? ` (conta de ${incidente.reportado_por_nome})` : ''}
                        {incidente.criado_em ? ` em ${fmtDataHora(incidente.criado_em)}` : ''}.{' '}
                        {vis.sst ? 'Complete a investigação (RG.10) abaixo.' : `Reveja o plano de ação e dê seguimento (estado / Criar ${nc.sigla}).`}
                    </div>
                )}
                {opcoesErro && <div style={{ ...aviso, background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}>Não foi possível carregar as listas de trabalhadores, subcontratados e obras.</div>}

                {/* Ocorrência — tipo (= ORIGEM do Forms), data, departamento, quem comunica, obra / projeto */}
                <div style={secTit}>{nSec('ocorrencia')} · Ocorrência</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                    {GRUPOS_TIPO.map(([grupo, tipos]) => (
                        <div key={grupo} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={grupoTit}>{grupo}</span>
                            {tipos.map(k => {
                                const v = TIPOS[k];
                                const sel = form.tipo === k;
                                return (
                                    <button data-liquid-secondary="true" key={k} type="button" aria-pressed={sel} onClick={() => upd('tipo', k)}
                                        style={{
                                            ...btnSec, fontWeight: 700,
                                            background: sel ? v.bg : '#fff', color: sel ? v.fg : '#475569',
                                            borderColor: sel ? v.fg : '#cbd5e1',
                                        }}>
                                        {sel ? '● ' : '○ '}<LiquidText>{v.label}</LiquidText>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                    <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                        {vis.sst
                            ? 'Incidente = desvio, condição/ato inseguro ou dano material sem lesão.'
                            : `Comunicação: sem sinistrado, lesão, riscos, baixa nem RG.10 — segue para o Plano de melhoria com “Criar ${nc.sigla}”.`}
                    </span>
                </div>
                <div style={grid4}>
                    <Campo label="Data *">
                        <input type="date" value={form.data_ocorrencia} onChange={e => upd('data_ocorrencia', e.target.value)} style={inpW} />
                    </Campo>
                    <Campo label="Hora">
                        <input type="time" value={form.hora} onChange={e => upd('hora', e.target.value)} style={inpW} />
                    </Campo>
                    <Campo label="Departamento / Área *">
                        <select value={form.departamento} onChange={e => upd('departamento', e.target.value)} style={inpW}>
                            <option value="">— escolher —</option>
                            {form.departamento && !DEPARTAMENTOS[form.departamento] && <option value={form.departamento}>{form.departamento}</option>}
                            {Object.entries(DEPARTAMENTOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </Campo>
                    <Campo label="Comunicado por">
                        <input value={form.reportado_nome} onChange={e => upd('reportado_nome', e.target.value)}
                            placeholder={incidente?.reportado_por_nome || 'nome de quem comunica'} style={inpW} />
                    </Campo>
                </div>
                <div style={grid3}>
                    <Campo label="Obra">
                        <select value={form.obra_id} onChange={e => upd('obra_id', e.target.value)} style={inpW}>
                            <option value="">— sem obra / instalações —</option>
                            {obraFora && <option value={form.obra_id}>{incidente?.obra_nome || `Obra #${form.obra_id}`}</option>}
                            {opcoes.obras.map(o => <option key={o.id} value={String(o.id)}>{o.posicao}{o.descricao ? ` — ${o.descricao}` : ''}</option>)}
                        </select>
                    </Campo>
                    <Campo label="Projeto (ID)">
                        <input value={form.projeto_ref} onChange={e => upd('projeto_ref', e.target.value)} placeholder="se aplicável" style={inpW} />
                    </Campo>
                    <Campo label={vis.sst ? 'Local do acidente / incidente' : 'Local'}>
                        <input value={form.local} onChange={e => upd('local', e.target.value)} placeholder="ex.: piso 2, armazém, via pública…" style={inpW} />
                    </Campo>
                </div>
                {vis.sst && (
                    <div style={{ ...grid3, alignItems: 'end' }}>
                        <Campo label="ID ocorrência (se aplicável)">
                            <input value={form.id_ocorrencia} onChange={e => upd('id_ocorrencia', e.target.value)} style={inpW} />
                        </Campo>
                        <label style={{ ...chkLbl, gridColumn: 'span 2', marginBottom: 8 }}>
                            <input type="checkbox" checked={form.in_itinere} onChange={e => upd('in_itinere', e.target.checked)} /><LiquidText>
                            In itinere (trajeto casa ↔ trabalho) — não conta para os índices de sinistralidade
                        </LiquidText></label>
                    </div>
                )}

                {/* Sinistrado / envolvido (só SST) */}
                {vis.sst && (
                    <>
                        <div style={secTit}>{nSec('envolvido')} · {form.tipo === 'acidente' ? 'Sinistrado' : 'Envolvido'}</div>
                        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                            {Object.entries(VINCULOS).map(([k, v]) => (
                                <label key={k} style={{ ...chkLbl, marginBottom: 0 }}>
                                    <input type="radio" name="vinculo" checked={form.vinculo === k} onChange={() => mudarVinculo(k)} /> {v}
                                </label>
                            ))}
                        </div>
                        {form.vinculo === 'interno' ? (
                            <div style={grid2}>
                                <Campo label="Trabalhador">
                                    <select value={form.trabalhador_id} onChange={e => escolherTrabalhador(e.target.value)} style={inpW}>
                                        <option value="">— selecionar —</option>
                                        {trabInativo && <option value={form.trabalhador_id}>{incidente?.trabalhador_nome || form.nome || `#${form.trabalhador_id}`}{sufixoFora}</option>}
                                        {opcoes.trabalhadores.map(t => <option key={t.id} value={String(t.id)}>{t.nome}</option>)}
                                    </select>
                                </Campo>
                                <Campo label="Nome">
                                    <input value={form.nome} onChange={e => upd('nome', e.target.value)} readOnly={!!form.trabalhador_id}
                                        placeholder="se não constar da lista" style={{ ...inpW, background: form.trabalhador_id ? '#f8fafc' : '#fff' }} />
                                </Campo>
                            </div>
                        ) : (
                            <div style={grid2}>
                                <Campo label="Empresa subcontratada">
                                    <select value={form.subcontratado_id} onChange={e => escolherSubcontratado(e.target.value)} style={inpW}>
                                        <option value="">— outra / não registada —</option>
                                        {subInativo && <option value={form.subcontratado_id}>{incidente?.subcontratado_nome || `#${form.subcontratado_id}`}{sufixoFora}</option>}
                                        {opcoes.subcontratados.map(s => <option key={s.id} value={String(s.id)}>{s.nome}</option>)}
                                    </select>
                                </Campo>
                                <Campo label="Nome do sinistrado / envolvido">
                                    <input value={form.nome} onChange={e => upd('nome', e.target.value)} style={inpW} />
                                </Campo>
                            </div>
                        )}
                        <div style={grid4}>
                            <Campo label="Categoria / função">
                                <input value={form.funcao} onChange={e => upd('funcao', e.target.value)} style={inpW} />
                            </Campo>
                            <Campo label="Empregador">
                                <input value={form.empregador} onChange={e => upd('empregador', e.target.value)} style={inpW} />
                            </Campo>
                            <Campo label="Sexo">
                                <select value={form.sexo} onChange={e => upd('sexo', e.target.value)} style={inpW}>
                                    <option value="">—</option>
                                    <option value="M">Masculino</option>
                                    <option value="F">Feminino</option>
                                </select>
                            </Campo>
                            <Campo label="Tarefa habitual?">
                                <SelSN value={form.tarefa_habitual} onChange={v => upd('tarefa_habitual', v)} />
                            </Campo>
                        </div>
                    </>
                )}

                {/* Testemunhas (todos os tipos; nas comunicações são opcionais) */}
                <div style={secTit}>{nSec('testemunhas')} · Testemunhas{vis.sst ? '' : ' (opcional)'}</div>
                {testemunhas.map((t, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr auto', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                        <select value={t.trabalhador_id} onChange={e => escolherTestemunha(idx, e.target.value)} style={inpW}>
                            <option value="">— nome livre / externo —</option>
                            {t.trabalhador_id && !opcoes.trabalhadores.some(x => String(x.id) === t.trabalhador_id)
                                && <option value={t.trabalhador_id}>{t.nome || `#${t.trabalhador_id}`}{sufixoFora}</option>}
                            {opcoes.trabalhadores.map(x => <option key={x.id} value={String(x.id)}>{x.nome}</option>)}
                        </select>
                        <input value={t.nome} onChange={e => updTestemunha(idx, { nome: e.target.value })} readOnly={!!t.trabalhador_id}
                            placeholder={`Testemunha ${idx + 1} — nome`} style={{ ...inpW, background: t.trabalhador_id ? '#f8fafc' : '#fff' }} />
                        <input value={t.funcao} onChange={e => updTestemunha(idx, { funcao: e.target.value })} placeholder="Categoria / função" style={inpW} />
                        <button data-liquid-secondary="true" type="button" onClick={() => setTestemunhas(prev => prev.filter((_, i) => i !== idx))} style={{ ...btnSec, padding: '6px 10px', color: '#e53e3e' }} title="Remover"><DeleteIcon /></button>
                    </div>
                ))}
                {testemunhas.length < MAX_TESTEMUNHAS && (
                    <button data-liquid-secondary="true" type="button" onClick={() => setTestemunhas(prev => [...prev, testemunhaVazia()])} style={{ ...btnSec, padding: '4px 10px', fontSize: '0.78rem' }}>+ Testemunha</button>
                )}

                {/* Descrição — circunstâncias (pergunta 6 do Forms) e descrição do incidente (pergunta 7) */}
                <div style={secTit}>{nSec('descricao')} · Descrição</div>
                <Campo style={{ marginBottom: 10 }} label={(
                    <>Descrição <span style={lblNota}>(para AT incluir testemunhas, local, horas, se teve tratamento hospitalar ou se foram acionados os meios de socorro)</span></>
                )}>
                    <textarea value={form.circunstancias} onChange={e => upd('circunstancias', e.target.value)} rows={3} style={{ ...inpW, resize: 'vertical' }} />
                </Campo>
                <Campo label="Descrição do incidente *" style={{ marginBottom: 10 }}>
                    <textarea value={form.descricao} onChange={e => upd('descricao', e.target.value)} rows={4} style={{ ...inpW, resize: 'vertical' }}
                        placeholder={vis.sst
                            ? 'Feridos, meios necessários, consequências, pessoas envolvidas, danos materiais, primeiros socorros, assistência médica, condições perigosas e/ou atos inseguros…'
                            : 'O que aconteceu, o que foi reclamado ou detetado, ou a melhoria / sugestão…'} />
                </Campo>
                {vis.sst && (
                    <>
                        <div style={{ ...grid3, alignItems: 'end' }}>
                            <Campo label="Atividade / tarefa em curso">
                                <input value={form.atividade} onChange={e => upd('atividade', e.target.value)} style={inpW} />
                            </Campo>
                            <Campo label="Primeiros socorros?">
                                <SelSN value={form.primeiros_socorros} onChange={v => upd('primeiros_socorros', v)} />
                            </Campo>
                            <Campo label="Assistência médica / tratamento hospitalar">
                                <SelSN value={form.assistencia_medica} onChange={v => upd('assistencia_medica', v)} />
                            </Campo>
                        </div>
                        <div style={{ ...grid2, gridTemplateColumns: vis.socorro ? '1fr 2fr' : '1fr', alignItems: 'end' }}>
                            {vis.socorro && (
                                <Campo label="Foram acionados os meios de socorro (112 / INEM)?">
                                    <SelSN value={form.meios_socorro} onChange={v => upd('meios_socorro', v)} />
                                </Campo>
                            )}
                            <Campo label="Danos materiais">
                                <input value={form.danos_materiais} onChange={e => upd('danos_materiais', e.target.value)} placeholder="equipamentos, viaturas, instalações…" style={inpW} />
                            </Campo>
                        </div>
                    </>
                )}

                {/* Plano de ação e melhoria + "A executar em" (perguntas 8 e 9 do Forms) */}
                <div style={secTit}>{nSec('plano')} · Plano de ação e melhoria</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 190px', gap: 10, marginBottom: 10, alignItems: 'start' }}>
                    <Campo label="Plano de ação e melhoria">
                        <textarea value={form.plano_acao} onChange={e => upd('plano_acao', e.target.value)} rows={3} style={{ ...inpW, resize: 'vertical' }}
                            placeholder="O que fazer para corrigir e evitar que se repita…" />
                    </Campo>
                    <Campo label="A executar em">
                        <input type="date" value={form.plano_prazo} onChange={e => upd('plano_prazo', e.target.value)} style={inpW} />
                    </Campo>
                </div>
                {vis.sst && (
                    <div style={{ ...nota, margin: '-4px 0 6px' }}>
                        Na investigação (RG.10), “Copiar para as medidas” passa este plano para as medidas a implementar.
                    </div>
                )}

                {/* Lesão (acidente / quase-acidente) e risco (tipos SST: coluna J do Mapa) */}
                {vis.sst && (
                    <>
                        <div style={secTit}>{nSec('risco')} · {vis.lesao ? 'Lesão e risco (RG.10)' : 'Risco (Mapa — col. J)'}</div>
                        {vis.lesao && (
                            <div style={grid2}>
                                <Campo label="Tipo de lesão">
                                    <input value={form.lesao} onChange={e => upd('lesao', e.target.value)} placeholder="ex.: corte, entorse, fratura, contusão…" style={inpW} />
                                </Campo>
                                <Campo label="Local da lesão (parte do corpo)">
                                    <input value={form.local_lesao} onChange={e => upd('local_lesao', e.target.value)} placeholder="ex.: mão esquerda, olhos…" style={inpW} />
                                </Campo>
                            </div>
                        )}
                        <label data-liquid-label="true" style={lbl}>Risco — {riscosSel.size} selecionado{riscosSel.size === 1 ? '' : 's'}</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4, border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                            {[...RISCOS_RG10, ...riscosExtra].map(r => (
                                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={riscosSel.has(r)} onChange={() => toggleSet(setRiscosSel, r)} />
                                    {r === RISCO_OUTRO ? 'Outro. Qual?' : r}
                                </label>
                            ))}
                        </div>
                        {(riscosSel.has(RISCO_COLAPSO) || riscosSel.has(RISCO_OUTRO)) && (
                            <div style={grid2}>
                                {riscosSel.has(RISCO_COLAPSO) ? (
                                    <Campo label="Colapso de — quê?">
                                        <input value={colapsoTxt} onChange={e => setColapsoTxt(e.target.value)} placeholder="ex.: andaime, vala, cofragem…" style={inpW} />
                                    </Campo>
                                ) : <div />}
                                {riscosSel.has(RISCO_OUTRO) ? (
                                    <Campo label="Outro — qual?">
                                        <input value={form.riscos_outro} onChange={e => upd('riscos_outro', e.target.value)} style={inpW} />
                                    </Campo>
                                ) : <div />}
                            </div>
                        )}
                    </>
                )}

                {/* Análise (Mapa, colunas O–R e U): similares e comunicação interna em todos; IPAR só SST */}
                <div style={secTit}>{nSec('analise')} · Análise</div>
                <div style={grid5}>
                    <Campo label={vis.sst ? 'Incidentes similares?' : 'Casos similares?'}><SelSN value={form.similares} onChange={v => upd('similares', v)} /></Campo>
                    {vis.sst && <Campo label="Controlo operacional?"><SelSN value={form.controlo_operacional} onChange={v => upd('controlo_operacional', v)} /></Campo>}
                    {vis.sst && <Campo label="Risco na matriz IPAR / AIA?"><SelSN value={form.risco_ipar} onChange={v => upd('risco_ipar', v)} /></Campo>}
                    {vis.sst && <Campo label="Reavaliação IPAR?"><SelSN value={form.reavaliacao_ipar} onChange={v => upd('reavaliacao_ipar', v)} /></Campo>}
                    <Campo label="Comunicação interna?"><SelSN value={form.comunicacao_interna} onChange={v => upd('comunicacao_interna', v)} /></Campo>
                </div>

                {/* Investigação (RG.10) — só SST; consequência e baixa só em acidente / incidente */}
                {vis.sst && (
                    <>
                        <div style={secTit}>{nSec('investigacao')} · Investigação (RG.10)</div>
                        <Campo label="Identificação de causas" style={{ marginBottom: 10 }}>
                            <textarea value={form.causas} onChange={e => upd('causas', e.target.value)} rows={3} style={{ ...inpW, resize: 'vertical' }} />
                        </Campo>
                        {vis.baixa ? (
                            <>
                                <div style={grid3}>
                                    <Campo label="Consequência">
                                        <select value={form.consequencia} onChange={e => upd('consequencia', e.target.value)} style={inpW}>
                                            {Object.entries(CONSEQUENCIAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </select>
                                    </Campo>
                                    {COM_GRAU.includes(form.consequencia) ? (
                                        <Campo label={`Grau de incapacidade ${CONSEQ_CURTA[form.consequencia]} (%)`}>
                                            <input type="number" min="0" max="100" step="0.01" value={form.grau_incapacidade} onChange={e => upd('grau_incapacidade', e.target.value)} style={inpW} />
                                        </Campo>
                                    ) : <div />}
                                    <label style={{ ...chkLbl, paddingTop: 20 }}>
                                        <input type="checkbox" checked={form.paragem_atividade} onChange={e => upd('paragem_atividade', e.target.checked)} />
                                        Paragem de atividade
                                    </label>
                                </div>
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10, background: '#f8fafc' }}>
                                    <div style={grid4}>
                                        <Campo label="Início da baixa">
                                            <input type="date" value={form.baixa_inicio} onChange={e => upd('baixa_inicio', e.target.value)} style={inpW} />
                                        </Campo>
                                        <Campo label="Fim da baixa">
                                            <input type="date" value={form.baixa_fim} min={form.baixa_inicio || undefined} onChange={e => upd('baixa_fim', e.target.value)} style={inpW} />
                                        </Campo>
                                        <Campo label="DB calendário">
                                            {baixa.auto
                                                ? <div style={calcBox}>{baixa.dias_perdidos}</div>
                                                : <input type="number" min="0" value={form.dias_perdidos} onChange={e => upd('dias_perdidos', e.target.value)} style={inpW} />}
                                        </Campo>
                                        <Campo label="DB dias úteis">
                                            {baixa.auto
                                                ? <div style={calcBox}>{baixa.dias_uteis}</div>
                                                : <input type="number" min="0" value={form.dias_uteis} onChange={e => upd('dias_uteis', e.target.value)} style={inpW} />}
                                        </Campo>
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: baixa.erro ? '#b91c1c' : '#475569' }}>
                                        {baixa.erro || (
                                            <>
                                                Horas não trabalhadas: <strong>{fmtNum(baixa.horas_perdidas)}</strong> (dias úteis × 8).{' '}
                                                {baixa.auto
                                                    ? 'Calculado a partir das datas: DB calendário = (fim − início) + 1; dias úteis = seg–sex no intervalo.'
                                                    : 'Sem as duas datas, indique os dias à mão.'}
                                                {baixa.dias_perdidos >= 30 && <span style={{ color: '#b91c1c', fontWeight: 700 }}> · ≥ 30 dias de baixa: acidente grave.</span>}
                                                {form.consequencia === 'sem_ita' && baixa.dias_perdidos > 0 && (
                                                    <span style={{ color: '#b45309', fontWeight: 600 }}>
                                                        {form.tipo === 'acidente'
                                                            ? ' · Há dias de baixa mas a consequência é “Sem ITA”: escolha ITA, ITP, IPP, IPA ou Mortal.'
                                                            : ' · Há dias de baixa num registo sem lesão: reveja o tipo ou os dias.'}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                {/* A ITA do Mapa é "≤ 30 dias": acima disso é caso grave */}
                                {form.consequencia === 'ita' && !baixa.erro && baixa.dias_perdidos > 30 && (
                                    <div style={{ ...aviso, background: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b', fontWeight: 600 }}><LiquidText>
                                        ⚠️ Baixa superior a 30 dias — é caso grave (comunicar à ACT/APCER)
                                    </LiquidText></div>
                                )}
                            </>
                        ) : (
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                                <label style={{ ...chkLbl, marginBottom: 0 }}>
                                    <input type="checkbox" checked={form.paragem_atividade} onChange={e => upd('paragem_atividade', e.target.checked)} />
                                    Paragem de atividade
                                </label>
                                <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>Quase-acidente: sem consequência nem baixa (no Mapa fica “Sem Baixa”).</span>
                            </div>
                        )}

                        <label data-liquid-label="true" style={lbl}>Anexos / participações</label>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                            {PARTICIPACOES.map(([k, l]) => (
                                <label key={k} style={{ ...chkLbl, marginBottom: 0 }}>
                                    <input type="checkbox" checked={participacoes.has(k)} onChange={() => toggleSet(setParticipacoes, k)} /> {l}
                                </label>
                            ))}
                            <input value={form.participacao_outro} onChange={e => upd('participacao_outro', e.target.value)} placeholder="Outro: …" style={{ ...inp, flex: '1 1 180px' }} />
                        </div>
                        {alertas.seguradora && (
                            <div style={{ ...aviso, background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}><LiquidText>
                                🟠 Acidente de trabalho: participar à </LiquidText><strong>seguradora</strong> no prazo de 24 h (P_GSI_03).
                            </div>
                        )}
                        {(alertas.act || alertas.apcer) && (
                            <div style={{ ...aviso, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}><LiquidText>
                                🔴 Ocorrência grave (mortal, IPA, IPP ou ≥ 30 dias de baixa): comunicar também
                                </LiquidText>{alertas.act ? <strong> à ACT (24 h)</strong> : ''}{alertas.act && alertas.apcer ? ' e' : ''}{alertas.apcer ? <strong> à APCER</strong> : ''}.
                            </div>
                        )}

                        <label style={{ ...lbl, marginTop: 10 }}>Conclusões / medidas a implementar</label>
                        <div style={{ overflowX: 'auto', marginBottom: 6 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', color: '#64748b', textAlign: 'left' }}>
                                        <th style={{ ...thMin, width: 40 }}>N.º</th>
                                        <th style={thMin}>Ação</th>
                                        <th style={{ ...thMin, width: 150 }}>Prazo</th>
                                        <th style={{ ...thMin, width: 180 }}>Responsável</th>
                                        <th style={{ ...thMin, width: 70, textAlign: 'center' }}>Concluída</th>
                                        <th style={{ ...thMin, width: 40 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {medidas.map((m, idx) => (
                                        <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '4px 6px', fontWeight: 700, color: '#64748b' }}>{idx + 1}</td>
                                            <td style={{ padding: '4px 6px' }}><input value={m.acao} onChange={e => updMedida(idx, { acao: e.target.value })} style={inpW} /></td>
                                            <td style={{ padding: '4px 6px' }}><input type="date" value={m.prazo} onChange={e => updMedida(idx, { prazo: e.target.value })} style={inpW} /></td>
                                            <td style={{ padding: '4px 6px' }}><input value={m.responsavel} onChange={e => updMedida(idx, { responsavel: e.target.value })} style={inpW} /></td>
                                            <td style={{ padding: '4px 6px', textAlign: 'center' }}><input type="checkbox" checked={m.concluida} onChange={e => updMedida(idx, { concluida: e.target.checked })} /></td>
                                            <td style={{ padding: '4px 6px' }}>
                                                <button data-liquid-secondary="true" type="button" onClick={() => setMedidas(prev => prev.filter((_, i) => i !== idx))} style={{ ...btnSec, padding: '4px 8px', color: '#e53e3e' }} title="Remover"><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                            <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button data-liquid-secondary="true" type="button" onClick={() => setMedidas(prev => [...prev, medidaVazia()])} style={{ ...btnSec, padding: '4px 10px', fontSize: '0.78rem' }}>+ Medida</button>
                                <button data-liquid-secondary="true" type="button" onClick={copiarPlanoParaMedidas} disabled={!planoTxt || planoNasMedidas}
                                    title={!planoTxt ? 'Preencha primeiro o plano de ação e melhoria'
                                        : planoNasMedidas ? 'O plano de ação já consta das medidas'
                                            : 'Acrescenta o plano de ação às medidas (prazo = “A executar em”, responsável = quem comunicou)'}
                                    style={{
                                        ...btnSec, padding: '4px 10px', fontSize: '0.78rem', color: '#1d4ed8', borderColor: '#bfdbfe',
                                        opacity: !planoTxt || planoNasMedidas ? 0.55 : 1, cursor: !planoTxt || planoNasMedidas ? 'default' : 'pointer',
                                    }}>
                                    <LiquidText>{planoNasMedidas ? '✓ Plano já nas medidas' : '⤵ Copiar para as medidas'}</LiquidText>
                                </button>
                            </div>
                            <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>Ações corretivas formais → “Criar NC” (Plano de melhoria, origem “Registo de incidente”).</span>
                        </div>

                        <div style={{ ...grid4, alignItems: 'end' }}>
                            <Campo label="Técnico de segurança">
                                <input value={form.tecnico} onChange={e => upd('tecnico', e.target.value)} style={inpW} />
                            </Campo>
                            <Campo label="Data da investigação">
                                <input type="date" value={form.data_investigacao} onChange={e => upd('data_investigacao', e.target.value)} style={inpW} />
                            </Campo>
                            <Campo label="Responsável de Sistema Integrado">
                                <input value={form.rsi_nome} onChange={e => upd('rsi_nome', e.target.value)} style={inpW} />
                            </Campo>
                            <Campo label="Data de validação (RSI)">
                                <input type="date" value={form.data_validacao_rsi} onChange={e => upd('data_validacao_rsi', e.target.value)} style={inpW} />
                            </Campo>
                        </div>
                    </>
                )}

                {/* Estado e seguimento (todos os tipos); nas comunicações, "Criar NC" em destaque */}
                <div style={secTit}>{nSec('seguimento')} · Estado e seguimento</div>
                <div style={{ display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', gap: 10, marginBottom: 10, alignItems: 'start' }}>
                    <Campo label="Estado">
                        <select value={form.estado} onChange={e => upd('estado', e.target.value)} style={inpW}>
                            {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </Campo>
                    <Campo label="Observações">
                        <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} rows={2} style={{ ...inpW, resize: 'vertical' }} />
                    </Campo>
                </div>
                {!vis.sst && (
                    <div style={{ ...aviso, background: '#f5f3ff', borderColor: '#ddd6fe', color: '#4c1d95', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        {incidente?.nc_id ? (
                            <span>
                                Em tratamento no Plano de melhoria:{' '}
                                <span data-liquid-tone={liquidTone('#ede9fe')} data-liquid-badge="true" style={{ ...badge, background: '#ede9fe', color: '#5b21b6' }}>{ncCodigo || `#${incidente.nc_id}`}</span>
                            </span>
                        ) : (
                            <>
                                <span style={{ flex: '1 1 320px' }}>
                                    Seguimento: a <strong>{nc.nome} ({nc.sigla})</strong> é aberta no Plano de melhoria,
                                    origem “{nc.origem}”, com {nc.conteudo} deste registo.
                                </span>
                                <button type="button" onClick={() => guardar(true)} disabled={saving || aProcessarFotos}
                                    title={`Grava ${novo ? 'o registo' : 'as alterações'} e cria a ${nc.nome} (Plano de melhoria)`}
                                    style={{ ...btnNc, opacity: (saving || aProcessarFotos) ? 0.6 : 1 }}>
                                    {novo ? `Guardar e criar ${nc.sigla}` : `Criar ${nc.sigla}`}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Fotos */}
                <div style={secTit}>{nSec('fotos')} · Fotos ({totalFotos}/{MAX_FOTOS})</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                    {fotosExistentes.map(f => {
                        const url = fotosUrls[f.idx];
                        return (
                            <div key={`e${f.idx}`} style={thumbBox}>
                                {url ? (
                                    <a href={url} target="_blank" rel="noreferrer" title="Abrir em tamanho real">
                                        <img src={url} alt={`Foto ${f.idx + 1}`} style={thumbImg} />
                                    </a>
                                ) : (
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}><LiquidText>{url === null ? '⚠ indisponível' : 'A carregar…'}</LiquidText></span>
                                )}
                                <button type="button" onClick={() => setFotosExistentes(prev => prev.filter(x => x.idx !== f.idx))} style={thumbDel} title="Retirar foto (apagada ao guardar)"><LiquidText>✕</LiquidText></button>
                            </div>
                        );
                    })}
                    {fotosNovas.map((src, idx) => (
                        <div key={`n${idx}`} style={{ ...thumbBox, borderColor: '#93c5fd' }}>
                            <img src={src} alt={`Nova foto ${idx + 1}`} style={thumbImg} />
                            <button type="button" onClick={() => setFotosNovas(prev => prev.filter((_, i) => i !== idx))} style={thumbDel} title="Retirar"><LiquidText>✕</LiquidText></button>
                        </div>
                    ))}
                    {totalFotos < MAX_FOTOS && (
                        <label style={{ ...btnSec, cursor: aProcessarFotos ? 'default' : 'pointer', opacity: aProcessarFotos ? 0.6 : 1 }}>
                            <LiquidText>{aProcessarFotos ? 'A processar…' : '📷 Juntar fotos'}</LiquidText>
                            <input type="file" accept="image/jpeg,image/png,image/webp,image/*" multiple onChange={juntarFotos} disabled={aProcessarFotos} style={{ display: 'none' }} />
                        </label>
                    )}
                </div>
                {!novo && fotosExistentes.length < fotosIniciais.length && (
                    <div style={{ fontSize: '0.74rem', color: '#b45309' }}>
                        {fotosIniciais.length - fotosExistentes.length} foto(s) retirada(s) — são apagadas ao guardar.
                    </div>
                )}

                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    {/* Tipos SST: RG.10 e Criar NC aqui (nas comunicações a NC está em "Estado e seguimento") */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {!novo && vis.sst && podeRg10(incidente) && (
                            <button data-liquid-secondary="true" type="button" onClick={() => abrirRg10(incidente)} style={btnSec} title="PDF do RG.10 com os dados gravados"><LiquidText>📄 RG.10</LiquidText></button>
                        )}
                        {!novo && vis.sst && (incidente.nc_id
                            ? <span data-liquid-tone={liquidTone('#ede9fe')} data-liquid-badge="true" style={{ ...badge, background: '#ede9fe', color: '#5b21b6' }} title="Não Conformidade associada">{ncCodigo || `NC #${incidente.nc_id}`}</span>
                            : <button data-liquid-secondary="true" type="button" onClick={() => guardar(true)} disabled={saving || aProcessarFotos} style={{ ...btnSec, color: '#5b21b6', borderColor: '#c4b5fd' }}
                                title={`Grava as alterações e cria a ${nc.nome} (Plano de melhoria)`}>{`Criar ${nc.sigla}`}</button>)}
                    </div>
                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                        <button data-liquid-secondary="true" type="button" onClick={onClose} style={btnSec}>Cancelar</button>
                        <button data-liquid-primary="true" type="button" onClick={() => guardar(false)} disabled={saving || aProcessarFotos}
                            style={{ ...btnPri, opacity: (saving || aProcessarFotos) ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                    </div>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Mapa de Incidentes M.GME.05.B/01 — colunas A..AC do Excel + AD..AI ---------- */
const marcaX = (b) => (b ? 'X' : '');
// Coluna K — texto do servidor quando vem (consequencia_texto, igual ao Excel), senão o espelho do linhaMapa
const textoConsequenciaMapa = (i) => {
    if (typeof i.consequencia_texto === 'string') return i.consequencia_texto || '—';
    let t;
    if (i.tipo === 'quase_acidente') t = ['Quase acidente', i.lesao].filter(Boolean).join(' | ');
    else if (i.tipo === 'incidente') t = ['Incidente', i.danos_materiais ? `Danos materiais: ${i.danos_materiais}` : null].filter(Boolean).join(' | ');
    else {
        const c = CONSEQ_CURTA[i.consequencia];
        const grau = COM_GRAU.includes(i.consequencia) && i.grau_incapacidade != null && i.grau_incapacidade !== ''
            ? ` ${fmtNum(i.grau_incapacidade, 2)}%` : '';
        t = [i.lesao, c ? c + grau : null].filter(Boolean).join(' | ');
    }
    return t || '—';
};
// c: centrado · grupo: por baixo de "Dias de baixa (DB)" (cabeçalho em 2 linhas)
// sst: coluna só dos tipos SST — fica vazia nas comunicações (quando incluídas no mapa do ecrã)
const COLUNAS_MAPA = [
    { k: 'A', t: 'N.º do Acidente', w: 100, r: i => i.numero || '—' },
    { k: 'B', t: 'Tipo de Incidente', w: 120, r: i => `${infoTipo(i.tipo).label}${i.in_itinere ? ' (in itinere)' : ''}` },
    { k: 'C', t: 'Data do Incidente', w: 96, r: i => `${fmtData(i.data_ocorrencia)}${i.hora ? ` ${i.hora}` : ''}`, c: true },
    { k: 'D', t: 'Local', w: 160, r: i => [i.obra_nome, i.local].filter(Boolean).join(' — ') || '—' },
    {
        k: 'E', t: 'Trabalhador (Interno / Subcontratado)', w: 130, sst: true,
        r: i => { const emp = i.subcontratado_nome || i.empregador; return i.vinculo === 'subcontratado' ? `Subcontratado${emp ? ` (${emp})` : ''}` : 'Interno'; },
    },
    { k: 'F', t: 'Acidentado(a)', w: 150, r: i => i.nome || i.trabalhador_nome || '—', sst: true },
    { k: 'G', t: 'Sexo', w: 50, r: i => i.sexo || '—', c: true, sst: true },
    { k: 'H', t: 'Função', w: 120, r: i => i.funcao || '—', sst: true },
    { k: 'I', t: 'Descrição do incidente', w: 260, r: i => i.descricao || '—' },
    { k: 'J', t: 'Risco', w: 180, r: textoRiscos, sst: true },
    { k: 'K', t: 'Consequência (Tipo de lesão | Quase acidente)', w: 160, r: textoConsequenciaMapa, sst: true },
    { k: 'L', t: 'Atividade / Tarefa que estava a ser desenvolvida', w: 170, r: i => i.atividade || '—', sst: true },
    { k: 'M', t: 'Local da Lesão', w: 110, r: i => i.local_lesao || '—', sst: true },
    { k: 'N', t: 'Tarefa Habitual para o Sinistrado?', w: 90, r: i => simNao(i.tarefa_habitual), c: true, sst: true },
    { k: 'O', t: 'Existem registos Incidentes similares?', w: 90, r: i => simNao(i.similares), c: true },
    { k: 'P', t: 'Existência Controlo Operacional?', w: 90, r: i => simNao(i.controlo_operacional), c: true, sst: true },
    { k: 'Q', t: 'Risco identificado na Matriz IPAR / AIA?', w: 90, r: i => simNao(i.risco_ipar), c: true, sst: true },
    { k: 'R', t: 'Reavaliação de riscos na Matriz IPAR?', w: 90, r: i => simNao(i.reavaliacao_ipar), c: true, sst: true },
    { k: 'S', t: 'Testemunhas', w: 170, r: textoTestemunhas },
    { k: 'T', t: 'ID Ocorrência (se aplicável)', w: 90, r: i => i.id_ocorrencia || '—', c: true, sst: true },
    { k: 'U', t: 'Comunicação Interna?', w: 90, r: i => simNao(i.comunicacao_interna), c: true },
    { k: 'V', t: 'Mortal?', w: 60, r: i => marcaX(i.consequencia === 'mortal'), c: true, sst: true },
    { k: 'W', t: 'Com Baixa (X)', w: 60, r: i => marcaX(COM_BAIXA.includes(i.consequencia)), c: true, sst: true },
    { k: 'X', t: 'Sem Baixa (X)', w: 60, r: i => marcaX(i.consequencia === 'sem_ita'), c: true, sst: true },
    { k: 'Y', t: 'Data início da baixa', w: 90, r: i => (i.baixa_inicio ? fmtData(i.baixa_inicio) : ''), c: true, sst: true },
    { k: 'Z', t: 'Data fim da baixa', w: 90, r: i => (i.baixa_fim ? fmtData(i.baixa_fim) : ''), c: true, sst: true },
    { k: 'AA', t: 'DB Calendário', w: 80, r: i => num(i.dias_perdidos), c: true, grupo: true, sst: true },
    { k: 'AB', t: 'DB dias úteis', w: 80, r: i => num(i.dias_uteis), c: true, grupo: true, sst: true },
    { k: 'AC', t: 'Horas Não Trabalhadas', w: 90, r: i => fmtNum(i.horas_perdidas), c: true, sst: true },
    // Registo de comunicação (perguntas do Forms) — as mesmas colunas extra do mapa.xlsx
    { k: 'AD', t: 'Departamento / Área', w: 110, r: i => nomeDepartamento(i.departamento) || '—' },
    { k: 'AE', t: 'Projeto (ID)', w: 100, r: i => i.projeto_ref || '—' },
    { k: 'AF', t: 'Comunicado por', w: 140, r: i => nomeComunicou(i) || '—' },
    { k: 'AG', t: 'Descrição (circunstâncias)', w: 240, r: i => i.circunstancias || '—' },
    { k: 'AH', t: 'Plano de ação e melhoria', w: 220, r: i => i.plano_acao || '—' },
    { k: 'AI', t: 'A executar em', w: 90, r: i => (i.plano_prazo ? fmtData(i.plano_prazo) : ''), c: true },
];
const I_AA = COLUNAS_MAPA.findIndex(col => col.k === 'AA');
const N_COLS_APOS_AC = COLUNAS_MAPA.length - (I_AA + 3);      // AD..AI (depois de AA, AB, AC)
const valorCelulaMapa = (col, i) => (col.sst && !eSst(i.tipo) ? '' : col.r(i));
// Formatação condicional do Excel: AA ≥ 30 a vermelho; K a vermelho quando é para comunicar à ACT/APCER
const estiloCelulaMapa = (col, i) => {
    if (col.k === 'AA' && num(i.dias_perdidos) >= 30) return { background: '#fee2e2', color: '#991b1b', fontWeight: 700 };
    if (col.k === 'K' && alertasDoRegisto(i).grave) return { background: '#fee2e2', color: '#991b1b', fontWeight: 600 };
    return null;
};

/* ---------- Componente principal ---------- */
const Acidentes = () => {
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [aba, setAba] = useState('registos');         // registos | mapa | indices
    const [filtroTipo, setFiltroTipo] = useState('todos');   // todos | sst | reclamacao | nao_conformidade | outro
    const [mapaComComunicacoes, setMapaComComunicacoes] = useState(false);
    const [incidentes, setIncidentes] = useState([]);
    const [indices, setIndices] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [modal, setModal] = useState(null);           // { incidente } | { tipo } | null
    const [edicaoMes, setEdicaoMes] = useState({});     // { mes: { colaboradores, horas } }
    const [aExportar, setAExportar] = useState(false);
    const [aGuardarMeses, setAGuardarMeses] = useState(false);
    const pedido = useRef(0);

    const carregar = useCallback(async () => {
        const meu = ++pedido.current;
        setLoading(true); setErro('');
        const [r, ri] = await Promise.allSettled([
            api.get('/sistema-integrado/incidentes', { params: { ano } }),
            api.get('/sistema-integrado/incidentes/indices', { params: { ano } }),
        ]);
        if (meu !== pedido.current) return;             // resposta de um ano entretanto trocado
        if (r.status === 'fulfilled') setIncidentes(Array.isArray(r.value.data) ? r.value.data : []);
        else { setIncidentes([]); setErro(r.reason?.response?.data?.error || 'Erro ao carregar os registos.'); }
        setIndices(ri.status === 'fulfilled' && ri.value.data ? ri.value.data : null);
        setLoading(false);
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);
    const mudarAno = (a) => { setAno(a); setEdicaoMes({}); };
    // Depois de gravar: se a ocorrência é de outro ano, passa para esse ano (senão o registo
    // "desaparecia" da lista e convidava a um registo duplicado)
    const aposGravar = (row) => {
        const a = parseInt(dISO(row?.data_ocorrencia).slice(0, 4), 10);
        if (a && a !== ano) mudarAno(a); else carregar();
    };

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 6; a--) anos.push(a);
    if (!anos.includes(ano)) { anos.push(ano); anos.sort((x, y) => y - x); }

    // Contagens por filtro (separador Registos) e lista filtrada
    const contagens = useMemo(() => {
        const c = { todos: incidentes.length, sst: 0, reclamacao: 0, nao_conformidade: 0, outro: 0 };
        incidentes.forEach(i => {
            if (eSst(i.tipo)) c.sst++;
            else if (TIPOS_COMUNICACAO.includes(i.tipo)) c[i.tipo]++;
        });
        return c;
    }, [incidentes]);
    const registosFiltrados = useMemo(() => {
        if (filtroTipo === 'todos') return incidentes;
        if (filtroTipo === 'sst') return incidentes.filter(i => eSst(i.tipo));
        return incidentes.filter(i => i.tipo === filtroTipo);
    }, [incidentes, filtroTipo]);
    // Mapa: por defeito só os tipos SST (como a folha "Incidentes" do Excel)
    const mapaOrdenado = useMemo(() => incidentes
        .filter(i => mapaComComunicacoes || eSst(i.tipo))
        .sort((a, b) => dISO(a.data_ocorrencia).localeCompare(dISO(b.data_ocorrencia)) || (a.id - b.id)),
    [incidentes, mapaComComunicacoes]);
    const totaisMapa = useMemo(() => ({
        dias: mapaOrdenado.reduce((s, i) => s + num(i.dias_perdidos), 0),
        uteis: mapaOrdenado.reduce((s, i) => s + num(i.dias_uteis), 0),
        horas: mapaOrdenado.reduce((s, i) => s + num(i.horas_perdidas), 0),
    }), [mapaOrdenado]);
    const nComunicacoes = incidentes.length - contagens.sst;

    const eliminar = async (i) => {
        if (!window.confirm(`Eliminar o registo ${i.numero || ''}? As fotos também são apagadas.`)) return;
        try { await api.delete(`/sistema-integrado/incidentes/${i.id}`); carregar(); }
        catch (e) { alert(e.response?.data?.error || 'Erro ao eliminar'); }
    };
    const criarNc = async (i) => {
        const nc = infoNc(i.tipo);
        if (!window.confirm(`Criar uma ${nc.nome} (origem "${nc.origem}") a partir de ${i.numero || 'este registo'}?\nPassam para a ${nc.sigla} ${nc.conteudo}.`)) return;
        try { await api.post(`/sistema-integrado/incidentes/${i.id}/criar-nc`); }
        catch (e) { alert(e.response?.data?.error || `Erro ao criar a ${nc.nome}`); }
        carregar();
    };
    const exportarMapa = async () => {
        setAExportar(true);
        try {
            const r = await api.get('/sistema-integrado/incidentes/mapa.xlsx', { params: { ano }, responseType: 'blob', timeout: 60000 });
            abrirOuDescarregar(r, `Mapa_Incidentes_${ano}.xlsx`);
        } catch (e) { alert(await erroDeBlob(e, 'Erro ao gerar o Mapa de Incidentes')); }
        finally { setAExportar(false); }
    };

    // Índices: colaboradores/horas por mês (edição local até gravar)
    const valorMes = (m, k) => {
        const ed = edicaoMes[m.mes];
        return ed && ed[k] !== undefined ? ed[k] : (m[k] ?? '');
    };
    const updMes = (mes, k, v) => setEdicaoMes(prev => ({ ...prev, [mes]: { ...(prev[mes] || {}), [k]: v } }));
    const normNum = (v) => {
        const s = v == null ? '' : String(v).trim();
        return s === '' ? null : s.replace(',', '.');
    };
    // Horas = colaboradores × 8 h × dias úteis (seg–sex) do mês, a 1 casa (NUMERIC(10,1), sem artefactos de vírgula flutuante)
    const horasDoMes = (colab, mes) => Math.round(colab * 8 * diasUteisMes(ano, mes) * 10) / 10;
    // Mês gravado com colaboradores e sem horas: grava também as horas calculadas (e passam a ver-se na tabela)
    const gravarMes = (m) => {
        const colaboradores = normNum(valorMes(m, 'colaboradores'));
        let horas = normNum(valorMes(m, 'horas'));
        const nColab = parseFloat(colaboradores);
        if (horas == null && Number.isFinite(nColab) && nColab > 0) horas = String(horasDoMes(nColab, m.mes));
        return api.put('/sistema-integrado/incidentes/sinistralidade', { ano, mes: m.mes, colaboradores, horas });
    };
    const guardarMes = async (m) => {
        try {
            await gravarMes(m);
            setEdicaoMes(prev => { const n = { ...prev }; delete n[m.mes]; return n; });
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao guardar'); }
    };
    const guardarTodosMeses = async () => {
        const meses = (indices?.meses || []).filter(m => edicaoMes[m.mes]);
        if (!meses.length) return;
        setAGuardarMeses(true);
        try {
            for (const m of meses) await gravarMes(m);
            setEdicaoMes({});
        } catch (e) { alert(e.response?.data?.error || 'Erro ao guardar'); }
        setAGuardarMeses(false);
        carregar();
    };
    // Horas = colaboradores × 8 h × dias úteis (seg–sex) do mês; sem colaboradores usa a sugestão (trabalhadores ativos)
    const calcularHoras = (m) => {
        let colab = parseFloat(String(valorMes(m, 'colaboradores')).replace(',', '.'));
        if (!Number.isFinite(colab) || colab <= 0) colab = num(indices?.trabalhadores_ativos);
        if (!colab) { alert('Indique o n.º médio de colaboradores do mês.'); return; }
        const horas = horasDoMes(colab, m.mes);
        setEdicaoMes(prev => ({ ...prev, [m.mes]: { ...(prev[m.mes] || {}), colaboradores: String(colab), horas: String(horas) } }));
    };

    const res = indices?.resumo;
    const tot = indices?.total;
    const nEdicoes = Object.keys(edicaoMes).length;
    // Comunicações do bloco-resumo (n_reclamacoes, n_nao_conformidades, n_outros); enquanto o servidor não as
    // devolver, conta-se a partir dos registos do ano (são os mesmos)
    const resumoCom = (campo, tipo) => (res ? (res[campo] ?? contagens[tipo]) : undefined);
    const cartoes = [
        { v: res?.n_acidentes, l: 'Acidentes', zero: true },
        { v: res?.n_quase, l: 'Quase-acidentes' },
        { v: res?.n_incidentes, l: 'Incidentes' },
        { v: resumoCom('n_reclamacoes', 'reclamacao'), l: 'Reclamações' },
        { v: resumoCom('n_nao_conformidades', 'nao_conformidade'), l: 'Não conformidades' },
        { v: resumoCom('n_outros', 'outro'), l: 'Outros' },
        { v: res?.n_com_baixa, l: 'Acidentes c/ baixa', zero: true },
        { v: res?.total_acid_quase, l: 'Total (acid. + quase)' },
        { v: res?.db_calendario, l: 'DB calendário', zero: true },
        { v: res?.db_uteis, l: 'DB dias úteis', zero: true },
        { v: res?.horas_nao_trabalhadas, l: 'Horas não trabalhadas', zero: true },
        { v: res?.media_dias_baixa, l: 'Média dias baixa / acidente', zero: true, dec: true },
        { v: tot?.indice_frequencia, l: 'Índice de frequência', zero: true, dec: true },
        { v: tot?.indice_gravidade, l: 'Índice de gravidade', zero: true, dec: true },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div>
                    <label data-liquid-label="true" style={lbl}>Ano</label>
                    <select value={ano} onChange={e => mudarAno(parseInt(e.target.value, 10))} style={inp}>
                        {anos.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button data-liquid-secondary="true" onClick={() => setModal({ tipo: 'reclamacao' })} style={btnSec}
                        title="Reclamação, não conformidade ou melhoria / sugestão (o tipo pode ser mudado no formulário)">+ Registar comunicação</button>
                    <button data-liquid-secondary="true" onClick={() => setModal({ tipo: 'incidente' })} style={btnSec}>+ Registar incidente</button>
                    <button data-liquid-primary="true" onClick={() => setModal({ tipo: 'acidente' })} style={btnPri}>+ Registar acidente</button>
                    <button data-liquid-secondary="true" onClick={exportarMapa} disabled={aExportar} style={{ ...btnSec, opacity: aExportar ? 0.6 : 1 }}
                        title={`Mapa de Incidentes M.GME.05.B/01 — ${ano} (folhas Incidentes, Comunicações e Índices de Sinistralidade)`}>
                        <LiquidText>{aExportar ? 'A gerar…' : '⬇ Mapa de Incidentes (Excel)'}</LiquidText>
                    </button>
                </div>
            </div>

            {/* Cartões-resumo (bloco-resumo do Mapa de Incidentes) */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {cartoes.map(c => {
                    const vazio = c.v == null;
                    const mau = c.zero && !vazio && num(c.v) > 0;
                    const neutro = !c.zero || vazio;
                    const cor = neutro ? '#475569' : mau ? '#991b1b' : '#166534';
                    return (
                        <div key={c.l} style={{
                            flex: '1 1 104px', borderRadius: 10, padding: '8px 10px', textAlign: 'center',
                            background: neutro ? '#f1f5f9' : mau ? '#fee2e2' : '#f0fdf4',
                            border: `1px solid ${neutro ? '#e2e8f0' : mau ? '#fecaca' : '#bbf7d0'}`,
                        }}>
                            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: cor }}>{vazio ? '—' : c.dec ? fmtIdx(c.v) : fmtNum(c.v)}</div>
                            <div style={{ fontSize: '0.72rem', color: cor, lineHeight: 1.2 }}>{c.l}</div>
                        </div>
                    );
                })}
            </div>

            {/* Separadores */}
            <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #e2e8f0', marginBottom: 14, flexWrap: 'wrap' }}>
                {[['registos', `Registos (${incidentes.length})`], ['mapa', 'Mapa de Incidentes'], ['indices', 'Índices de sinistralidade']].map(([k, l]) => (
                    <button key={k} onClick={() => setAba(k)} style={tabBtn(aba === k)}>{l}</button>
                ))}
            </div>

            {erro && <div style={{ ...aviso, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{erro}</div>}

            {aba === 'registos' && (loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : incidentes.length === 0 ? (erro ? null : (
                    <div data-liquid-notice="true" data-liquid-tone={liquidTone('#f0fdf4')} style={{ padding: '26px 0 30px', textAlign: 'center', color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}><LiquidText>
                        ✅ Sem acidentes, incidentes ou comunicações registados em </LiquidText>{ano}.
                    </div>
                )) : (
                    <>
                        {/* Filtro por tipo */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                            {FILTROS_TIPO.map(([k, l]) => (
                                <button key={k} type="button" aria-pressed={filtroTipo === k} onClick={() => setFiltroTipo(k)} style={chip(filtroTipo === k)}>
                                    {l} <span style={{ opacity: 0.65 }}>({contagens[k]})</span>
                                </button>
                            ))}
                        </div>
                        {registosFiltrados.length === 0 ? (
                            <p style={{ color: '#94a3b8', margin: '14px 0' }}>Sem registos deste tipo em {ano}.</p>
                        ) : (
                            <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                                <table className="standard-table">
                                    <thead>
                                        <tr>
                                            <th>N.º</th>
                                            <th>Tipo</th>
                                            <th>Data</th>
                                            <th>Departamento / Área</th>
                                            <th>Envolvido / comunicado por</th>
                                            <th>Obra / projeto · local</th>
                                            <th>Descrição</th>
                                            <th>Consequência</th>
                                            <th style={{ textAlign: 'center' }}>Dias</th>
                                            <th>Estado</th>
                                            <th>A executar em</th>
                                            <th>Alertas</th>
                                            <th>NC</th>
                                            <th style={{ textAlign: 'right' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {registosFiltrados.map(i => {
                                            const tp = infoTipo(i.tipo);
                                            const sst = eSst(i.tipo);
                                            const es = ESTADOS[i.estado] || ESTADOS.aberto;
                                            const al = alertasDoRegisto(i);
                                            const faltaAct = [al.act && 'ACT', al.apcer && 'APCER'].filter(Boolean).join(' e ');
                                            const atraso = planoEmAtraso(i);
                                            const dias = num(i.dias_perdidos);
                                            const grau = i.grau_incapacidade != null && COM_GRAU.includes(i.consequencia) ? ` ${fmtNum(i.grau_incapacidade, 2)}%` : '';
                                            const obraLocal = [i.obra_nome, i.local].filter(Boolean).join(' — ');
                                            const nc = infoNc(i.tipo);
                                            return (
                                                <tr key={i.id}>
                                                    <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{i.numero || '—'}</td>
                                                    <td>
                                                        <span data-liquid-tone={liquidTone(tp.bg)} data-liquid-badge="true" style={{ ...badge, background: tp.bg, color: tp.fg }} title={tp.label}>{tp.curto}</span>
                                                        {i.in_itinere && <span data-liquid-badge="true" data-liquid-tone={liquidTone('#f1f5f9')} style={{ ...badgeMini, background: '#f1f5f9', color: '#475569' }}>in itinere</span>}
                                                        {i.origem === 'mobile' && <span data-liquid-badge="true" data-liquid-tone={liquidTone('#eff6ff')} style={{ ...badgeMini, background: '#eff6ff', color: '#1d4ed8' }} title={`Reportado por ${nomeComunicou(i) || '—'}`}><LiquidText>📱 mobile</LiquidText></span>}
                                                        {i.origem === 'mobile' && i.estado === 'aberto' && <span data-liquid-badge="true" data-liquid-tone={liquidTone('#dc2626')} style={{ ...badgeMini, background: '#dc2626', color: '#fff' }}>novo</span>}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{fmtData(i.data_ocorrencia)}{i.hora ? <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{i.hora}</div> : null}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{nomeDepartamento(i.departamento) || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                                    <td>
                                                        {sst ? (
                                                            <>
                                                                {nomeEnvolvido(i)}
                                                                <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                                                                    {VINCULOS[i.vinculo] || 'Interno'}{i.vinculo === 'subcontratado' && (i.subcontratado_nome || i.empregador) ? ` — ${i.subcontratado_nome || i.empregador}` : ''}
                                                                </div>
                                                                {nomeComunicou(i) && <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>comunicado por {nomeComunicou(i)}</div>}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {nomeComunicou(i) || '—'}
                                                                <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>comunicado por</div>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td style={{ maxWidth: 200 }}>
                                                        {obraLocal || (i.projeto_ref ? null : '—')}
                                                        {i.projeto_ref && <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Projeto: {i.projeto_ref}</div>}
                                                    </td>
                                                    <td style={{ maxWidth: 280 }}
                                                        title={[i.descricao, i.circunstancias && `Descrição (circunstâncias): ${i.circunstancias}`, i.plano_acao && `Plano de ação e melhoria: ${i.plano_acao}`].filter(Boolean).join('\n\n')}>
                                                        {(i.descricao || '').length > 160 ? `${i.descricao.slice(0, 160)}…` : i.descricao}
                                                        {i.plano_acao && (
                                                            <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                                                                Plano: {i.plano_acao.length > 100 ? `${i.plano_acao.slice(0, 100)}…` : i.plano_acao}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{i.tipo === 'acidente' ? `${CONSEQ_CURTA[i.consequencia] || '—'}${grau}` : '—'}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: 600, color: dias >= 30 ? '#b91c1c' : undefined }}>{sst ? dias : <span style={{ color: '#cbd5e1', fontWeight: 400 }}>—</span>}</td>
                                                    <td><span data-liquid-tone={liquidTone(es.bg)} data-liquid-badge="true" style={{ ...badge, background: es.bg, color: es.fg }}><LiquidText>{es.label}</LiquidText></span></td>
                                                    <td style={{ whiteSpace: 'nowrap', color: atraso ? '#b45309' : undefined, fontWeight: atraso ? 600 : undefined }}>
                                                        {i.plano_prazo ? fmtData(i.plano_prazo) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                    </td>
                                                    <td style={{ fontSize: '0.76rem', whiteSpace: 'nowrap' }}>
                                                        {faltaAct && <div style={{ color: '#b91c1c', fontWeight: 600 }} title="Acidente grave: comunicar à ACT (24 h) e à APCER"><LiquidText>🔴 </LiquidText>{faltaAct} em falta</div>}
                                                        {al.seguradora && <div style={{ color: '#c2410c', fontWeight: 600 }} title="Acidente de trabalho: participar à seguradora em 24 h"><LiquidText>🟠 Seguradora em falta</LiquidText></div>}
                                                        {atraso && <div style={{ color: '#b45309', fontWeight: 600 }} title={`Plano de ação a executar em ${fmtData(i.plano_prazo)} e registo ainda não fechado`}><LiquidText>⏰ Plano em atraso</LiquidText></div>}
                                                        {!faltaAct && !al.seguradora && !atraso && <span style={{ color: '#cbd5e1' }}>—</span>}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        {i.nc_id ? <span data-liquid-tone={liquidTone('#ede9fe')} data-liquid-badge="true" style={{ ...badge, background: '#ede9fe', color: '#5b21b6' }}>{i.nc_codigo || `#${i.nc_id}`}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                    </td>
                                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                        <button data-liquid-primary="true" onClick={() => setModal({ incidente: i })} style={{ ...btnPri, ...mini }} title={sst ? 'Abrir / investigar' : 'Abrir'}>Abrir</button>
                                                        {podeRg10(i) && <button data-liquid-secondary="true" onClick={() => abrirRg10(i)} style={{ ...btnSec, ...mini }} title="RG.10 — Relatório de Investigação (PDF)"><LiquidText>📄 RG.10</LiquidText></button>}
                                                        {!i.nc_id && (
                                                            <button onClick={() => criarNc(i)} style={sst ? { ...btnSec, ...mini, color: '#5b21b6' } : { ...btnNc, ...mini }}
                                                                title={`Criar ${nc.nome} (Plano de melhoria)`}>{`Criar ${nc.sigla}`}</button>
                                                        )}
                                                        <button data-liquid-secondary="true" onClick={() => eliminar(i)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar"><DeleteIcon /></button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                ))}

            {aba === 'mapa' && (loading ? <p style={{ color: '#718096' }}>A carregar…</p> : (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                        <div style={{ ...cxTit, marginBottom: 0 }}>Mapa de Incidentes M.GME.05.B/01 — {ano}</div>
                        <label style={{ ...chkLbl, marginBottom: 0, fontSize: '0.8rem' }}>
                            <input type="checkbox" checked={mapaComComunicacoes} onChange={e => setMapaComComunicacoes(e.target.checked)} />
                            Incluir comunicações (reclamações, não conformidades, outros){nComunicacoes ? ` — ${nComunicacoes}` : ''}
                        </label>
                    </div>
                    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                        <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.76rem', minWidth: '100%' }}>
                            <thead>
                                <tr>
                                    {COLUNAS_MAPA.map((col, ci) => {
                                        if (col.k === 'AB') return null;
                                        if (col.k === 'AA') {
                                            return <th key="DB" colSpan={2} style={{ ...thMapa, textAlign: 'center' }}>Dias de baixa (DB)</th>;
                                        }
                                        return (
                                            <th key={col.k} rowSpan={2} style={{ ...thMapa, minWidth: col.w, ...(ci === 0 ? { ...colFixa, zIndex: 3, background: '#e2e8f0' } : null) }}>
                                                <span style={letraCol}>{col.k}</span>{col.t}
                                            </th>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    {COLUNAS_MAPA.filter(col => col.grupo).map(col => (
                                        <th key={col.k} style={{ ...thMapa, minWidth: col.w }}><span style={letraCol}>{col.k}</span>{col.t}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mapaOrdenado.length === 0 && !erro && (
                                    <tr><td colSpan={COLUNAS_MAPA.length} style={{ ...tdMapa, textAlign: 'center', color: '#94a3b8', padding: 18 }}>
                                        {mapaComComunicacoes ? `Sem registos em ${ano}.` : `Sem acidentes, quase-acidentes ou incidentes SST em ${ano}.`}
                                    </td></tr>
                                )}
                                {mapaOrdenado.map(i => (
                                    <tr key={i.id} onClick={() => setModal({ incidente: i })} style={{ cursor: 'pointer' }} title="Abrir o registo">
                                        {COLUNAS_MAPA.map((col, ci) => (
                                            <td key={col.k} style={{
                                                ...tdMapa, minWidth: col.w, textAlign: col.c ? 'center' : 'left',
                                                ...(ci === 0 ? { ...colFixa, fontWeight: 700 } : null),
                                                ...estiloCelulaMapa(col, i),
                                            }}>{valorCelulaMapa(col, i)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                            {mapaOrdenado.length > 0 && (
                                <tfoot>
                                    <tr style={{ fontWeight: 700 }}>
                                        <td style={{ ...tdMapa, ...colFixa, background: '#f1f5f9' }}>Total</td>
                                        <td colSpan={I_AA - 1} style={{ ...tdMapa, background: '#f1f5f9', color: '#64748b', fontWeight: 600 }}>
                                            {mapaOrdenado.length} registo{mapaOrdenado.length === 1 ? '' : 's'}
                                        </td>
                                        <td style={{ ...tdMapa, background: totaisMapa.dias >= 30 ? '#fee2e2' : '#f1f5f9', textAlign: 'center' }}>{fmtNum(totaisMapa.dias)}</td>
                                        <td style={{ ...tdMapa, background: '#f1f5f9', textAlign: 'center' }}>{fmtNum(totaisMapa.uteis)}</td>
                                        <td style={{ ...tdMapa, background: '#f1f5f9', textAlign: 'center' }}>{fmtNum(totaisMapa.horas)}</td>
                                        {N_COLS_APOS_AC > 0 && <td colSpan={N_COLS_APOS_AC} style={{ ...tdMapa, background: '#f1f5f9' }} />}
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                    <p style={nota}>
                        AA = (Z − Y) + 1 · AB = dias úteis (seg–sex) entre Y e Z · AC = AB × 8. AA ≥ 30 e consequências a comunicar à ACT/APCER a vermelho.
                        AD–AI: perguntas do registo de comunicação (departamento, projeto, quem comunicou, circunstâncias, plano de ação e prazo).
                        Por defeito só os tipos SST, como a folha “Incidentes” do Excel (as comunicações estão na folha “Comunicações”).
                        Clique numa linha para abrir o registo.
                    </p>
                </div>
            ))}

            {aba === 'indices' && (loading ? <p style={{ color: '#718096' }}>A carregar…</p> : !indices ? (
                <p style={{ color: '#b91c1c' }}>Não foi possível calcular os índices de {ano}.</p>
            ) : (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <div style={{ ...cxTit, marginBottom: 0 }}>Índices de sinistralidade {ano}</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            {indices.trabalhadores_ativos != null && (
                                <span style={{ fontSize: '0.76rem', color: '#64748b' }}>Sugestão: {indices.trabalhadores_ativos} trabalhadores ativos</span>
                            )}
                            {nEdicoes > 0 && (
                                <button data-liquid-primary="true" onClick={guardarTodosMeses} disabled={aGuardarMeses} style={{ ...btnPri, padding: '4px 12px', opacity: aGuardarMeses ? 0.6 : 1 }}>
                                    {aGuardarMeses ? 'A guardar…' : `Guardar ${nEdicoes} ${nEdicoes === 1 ? 'mês' : 'meses'}`}
                                </button>
                            )}
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                            <thead>
                                <tr style={{ textAlign: 'center', color: '#64748b', background: '#f8fafc' }}>
                                    <th style={{ ...thIdx, textAlign: 'left' }}>Mês</th>
                                    <th style={thIdx} title="N.º médio de colaboradores">Colab.</th>
                                    <th style={thIdx} title="Horas trabalhadas = colaboradores × 8 × dias úteis do mês">Horas</th>
                                    <th style={thIdx} title="Acidentes mortais">M</th>
                                    <th style={thIdx} title="Não mortais sem baixa">NM s/ baixa</th>
                                    <th style={thIdx} title="Não mortais com baixa">NM c/ baixa</th>
                                    <th style={thIdx} title="M + NM s/ baixa + NM c/ baixa">Total</th>
                                    <th style={thIdx} title="Total acumulado">Acum.</th>
                                    <th style={thIdx} title="Dias perdidos (calendário)">Dias perdidos</th>
                                    <th style={thIdx} title="IF do mês">IF</th>
                                    <th style={thIdx} title="IG do mês">IG</th>
                                    <th style={thIdx} title="(Acum. NM c/ baixa ÷ acum. horas) × 10⁶">IF acum.</th>
                                    <th style={thIdx} title="(Acum. dias ÷ acum. horas) × 10⁶">IG acum.</th>
                                    <th style={thIdx} title="Índice de incidência = (total acumulado ÷ colaboradores) × 10³">II</th>
                                    <th style={thIdx} title="Índice de avaliação da gravidade = acum. dias ÷ total acumulado">IAG</th>
                                    <th style={thIdx}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(indices.meses || []).map(m => {
                                    const editado = !!edicaoMes[m.mes];
                                    const du = diasUteisMes(ano, m.mes);
                                    return (
                                        <tr key={m.mes} style={{ borderTop: '1px solid #f1f5f9', textAlign: 'center', background: editado ? '#fffbeb' : undefined }}>
                                            <td style={{ ...tdIdx, textAlign: 'left', fontWeight: 600 }}>{MESES[m.mes - 1]}</td>
                                            <td style={tdIdx}>
                                                <input value={valorMes(m, 'colaboradores')} onChange={e => updMes(m.mes, 'colaboradores', e.target.value)}
                                                    placeholder={indices.trabalhadores_ativos != null ? String(indices.trabalhadores_ativos) : ''}
                                                    style={{ ...inp, width: 60, padding: '3px 6px', textAlign: 'center' }} />
                                            </td>
                                            <td style={{ ...tdIdx, whiteSpace: 'nowrap' }}>
                                                <input value={valorMes(m, 'horas')} onChange={e => updMes(m.mes, 'horas', e.target.value)}
                                                    placeholder={m.horas_estimadas != null ? `≈ ${fmtNum(m.horas_estimadas)}` : ''}
                                                    title={m.horas_estimadas != null ? `Estimativa usada nos índices: colab. × 8 h × ${du} dias úteis. Ao guardar o mês, as horas ficam gravadas.` : undefined}
                                                    style={{ ...inp, width: 78, padding: '3px 6px', textAlign: 'center' }} />
                                                <button data-liquid-secondary="true" onClick={() => calcularHoras(m)} style={{ ...btnSec, padding: '2px 7px', marginLeft: 4, fontSize: '0.72rem' }}
                                                    title={`Colab. × 8 h × ${du} dias úteis (seg–sex)`}>calcular</button>
                                            </td>
                                            <td style={tdIdx}>{m.mortais}</td>
                                            <td style={tdIdx}>{m.nm_sem_baixa}</td>
                                            <td style={tdIdx}>{m.nm_com_baixa}</td>
                                            <td style={{ ...tdIdx, fontWeight: 600 }}>{m.total ?? '—'}</td>
                                            <td style={tdIdx} title={`NM c/ baixa acum.: ${m.acum_nm_com_baixa ?? '—'} · dias acum.: ${m.acum_dias ?? '—'} · horas acum.: ${m.acum_horas != null ? fmtNum(m.acum_horas) : '—'}`}>{m.acum_total ?? '—'}</td>
                                            <td style={tdIdx}>{m.dias_perdidos}</td>
                                            <td style={tdIdx}>{fmtIdx(m.indice_frequencia)}</td>
                                            <td style={tdIdx}>{fmtIdx(m.indice_gravidade)}</td>
                                            <td style={{ ...tdIdx, fontWeight: 600 }}>{fmtIdx(m.if_acumulado)}</td>
                                            <td style={{ ...tdIdx, fontWeight: 600 }}>{fmtIdx(m.ig_acumulado)}</td>
                                            <td style={{ ...tdIdx, fontWeight: 600 }}>{fmtIdx(m.indice_incidencia)}</td>
                                            <td style={{ ...tdIdx, fontWeight: 600 }}>{fmtIdx(m.iag)}</td>
                                            <td style={tdIdx}>
                                                {editado && <button data-liquid-primary="true" onClick={() => guardarMes(m)} style={{ ...btnPri, padding: '3px 10px', fontSize: '0.75rem' }}>Guardar</button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                                <tr style={{ borderTop: '2px solid #e2e8f0', textAlign: 'center', fontWeight: 700, background: '#f8fafc' }}>
                                    <td style={{ ...tdIdx, textAlign: 'left' }}>Total anual</td>
                                    <td style={tdIdx} title="Média dos meses com colaboradores">{tot?.colaboradores_medio != null ? fmtNum(tot.colaboradores_medio) : '—'}</td>
                                    <td style={tdIdx}>{fmtNum(tot?.horas ?? 0)}</td>
                                    <td style={tdIdx}>{tot?.mortais ?? 0}</td>
                                    <td style={tdIdx}>{tot?.nm_sem_baixa ?? 0}</td>
                                    <td style={tdIdx}>{tot?.nm_com_baixa ?? 0}</td>
                                    <td style={tdIdx}>{tot?.total ?? '—'}</td>
                                    <td style={tdIdx}>—</td>
                                    <td style={tdIdx}>{tot?.dias_perdidos ?? 0}</td>
                                    <td style={tdIdx}>{fmtIdx(tot?.indice_frequencia)}</td>
                                    <td style={tdIdx}>{fmtIdx(tot?.indice_gravidade)}</td>
                                    <td style={tdIdx}>—</td>
                                    <td style={tdIdx}>—</td>
                                    <td style={tdIdx}>{fmtIdx(tot?.indice_incidencia)}</td>
                                    <td style={tdIdx}>{fmtIdx(tot?.iag)}</td>
                                    <td style={tdIdx}></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p style={nota}>
                        Só contam acidentes de trabalho (quase-acidentes, incidentes, comunicações e acidentes in itinere ficam de fora).
                        Horas = colaboradores × 8 h × dias úteis (seg–sex) do mês — ao guardar um mês com colaboradores e sem horas, são calculadas e gravadas.
                        IF acum. = (acum. NM c/ baixa ÷ acum. horas) × 10⁶ · IG acum. = (acum. dias perdidos ÷ acum. horas) × 10⁶ ·
                        II = (total acumulado ÷ n.º médio de colaboradores) × 10³ · IAG = acum. dias perdidos ÷ total acumulado.
                        IF/IG = valores do próprio mês. Total anual: IF = (NM c/ baixa ÷ horas) × 10⁶ · IG = (dias ÷ horas) × 10⁶ ·
                        II = (total ÷ média de colaboradores) × 10³ · IAG = dias ÷ total. Metas {ano} (RG.07): IF 0 · IG 0.
                    </p>
                </div>
            ))}

            {modal && (
                <IncidenteModal
                    key={modal.incidente?.id || `novo-${modal.tipo}`}
                    incidente={modal.incidente}
                    tipoInicial={modal.tipo}
                    onClose={() => setModal(null)}
                    onSaved={aposGravar}
                />
            )}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.72rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.88rem', boxSizing: 'border-box' };
const inpW = { ...inp, width: '100%' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const badgeMini = { ...badge, padding: '2px 7px', marginLeft: 4, fontSize: '0.68rem', display: 'inline-block' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
// "Criar NC" em destaque (comunicações)
const btnNc = { padding: '7px 14px', border: '1px solid #6d28d9', background: '#6d28d9', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
const mini = { padding: '4px 9px', marginLeft: 6, fontSize: '0.76rem' };
const chip = (ativo) => ({
    padding: '4px 12px', borderRadius: 999, cursor: 'pointer', fontSize: '0.78rem', fontWeight: ativo ? 700 : 500,
    border: `1px solid ${ativo ? '#1d4ed8' : '#cbd5e1'}`, background: ativo ? '#eff6ff' : '#fff', color: ativo ? '#1d4ed8' : '#475569',
});
const grupoTit = { minWidth: 118, fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' };
// Parte explicativa de um rótulo (sem maiúsculas)
const lblNota = { textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: '#94a3b8' };
const tabBtn = (ativo) => ({
    padding: '8px 16px', border: 'none', borderBottom: `3px solid ${ativo ? '#3182ce' : 'transparent'}`, background: 'transparent',
    cursor: 'pointer', fontSize: '0.88rem', fontWeight: ativo ? 700 : 500, color: ativo ? '#1d4ed8' : '#64748b', marginBottom: -1,
});
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 };
const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 };
const grid5 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 10 };
const secTit = { fontSize: '0.8rem', fontWeight: 700, color: '#3182ce', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e2e8f0', padding: '4px 0', margin: '16px 0 10px' };
const chkLbl = { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#334155', cursor: 'pointer', marginBottom: 6 };
const aviso = { border: '1px solid', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', marginBottom: 10 };
const calcBox = { ...inp, width: '100%', background: '#eef2ff', color: '#3730a3', fontWeight: 700, textAlign: 'center' };
const thMin = { padding: '6px', fontWeight: 600, fontSize: '0.74rem' };
const thumbBox = { position: 'relative', width: 96, height: 96, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' };
const thumbImg = { width: 96, height: 96, objectFit: 'cover', display: 'block' };
const thumbDel = { position: 'absolute', top: 3, right: 3, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.7)', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', lineHeight: '22px', padding: 0 };
const cxTit = { fontSize: '0.78rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 };
const nota = { fontSize: '0.74rem', color: '#94a3b8', margin: '8px 0 0', lineHeight: 1.5 };
const thMapa = { padding: '6px 8px', background: '#f1f5f9', color: '#334155', fontWeight: 700, fontSize: '0.72rem', verticalAlign: 'bottom', textAlign: 'left', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #cbd5e1', lineHeight: 1.25 };
const tdMapa = { padding: '5px 8px', borderRight: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', background: '#fff', whiteSpace: 'pre-wrap' };
const colFixa = { position: 'sticky', left: 0, zIndex: 1, boxShadow: '1px 0 0 #cbd5e1', whiteSpace: 'nowrap' };
const letraCol = { display: 'block', fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, marginBottom: 2 };
const thIdx = { padding: '6px 6px', fontWeight: 600 };
const tdIdx = { padding: '4px 6px' };

export default Acidentes;
